import axios from 'axios';
import {
  LoginResponse,
  Message,
  ChatHistory,
  SendMessageResponse,
  ConversationSummary,
  Conversation,
  ConversationCreateResponse,
  ConversationTagsResponse,
  User,
  StudentLoginResponse,
  StudentLoginRequest,
  StudentOptions,
  StudentWithConversation,
  PaginatedStudentConversations,
  ConversationWithMessages,
  AnalysisStatus,
  JobStatus,
  UserPersona,
  DashboardResponse,
  StudentDailyMetricsResponse,
  MetricsRefreshResponse,
  Student,
  ConversationLookupResponse,
  TeacherAuthResponse,
  TeacherRegisterRequest,
  Teacher,
  TeacherClassItem,
  QuizEligibility,
  ConversationQuiz,
  QuizSubmitResponse,
  RegisterRequest,
  AuthResponse,
  GradeChaptersResponse,
  TeacherSubject,
  TeacherCurriculumResponse,
  PlannedTopic,
  VoiceTopicCandidate,
  ReinforcementStudent,
  SyllabusItem,
  TeacherAssessmentList,
  TeacherAssessmentDetail,
  TeacherAssessmentSection,
  TopicCatalog,
  AvailableAssessment,
  StudentAssessmentDetail,
  AssessmentResult,
  StudentHomeworkItem,
  MarkHomeworkDoneResponse,
  StudentProjectItem,
  ClassHomeworkStats,
} from '../types';

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_BASE_URL + '/api' || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authorization token to requests if the student `user` is logged in.
// IMPORTANT: respect a pre-set Authorization header — teacher endpoints pass a
// JWT explicitly (e.g. `Authorization: Bearer <teacher_jwt>`), and we must NOT
// clobber it with the student bearer when both sessions are in localStorage.
API.interceptors.request.use((config) => {
  const alreadySet = (config.headers as any)?.Authorization || (config.headers as any)?.authorization;
  if (alreadySet) {
    return config;
  }
  // Prefer the student ACCESS JWT (new session model). Fall back to the legacy
  // bare `user.id` for sessions created before the JWT rollout — the backend
  // still accepts it while STUDENT_ALLOW_LEGACY_ID_TOKEN is on. (STUDENT_TOKEN_KEY
  // is declared lower in the file; this callback only runs at request time, well
  // after module init, so the reference resolves fine.)
  const studentToken = localStorage.getItem(STUDENT_TOKEN_KEY);
  if (studentToken) {
    config.headers.Authorization = `Bearer ${studentToken}`;
    return config;
  }
  const userJson = localStorage.getItem('user');
  if (userJson) {
    const user = JSON.parse(userJson);
    config.headers.Authorization = `Bearer ${user.id}`;
  }
  return config;
});

// --- Student auth: username + password ------------------------------------
//
// Onboarding flow (post-migration b5e8d1a92c34):
//   loginStudent     — POST /auth/login         { username, password }
//   registerStudent  — POST /auth/register      { username, password, school, grade, section, roll_number, first_name }
//   resetPassword    — POST /auth/reset-password { current_password, new_password }  (authenticated)
//
// All three return AuthResponse; the user object includes a `must_reset_password`
// flag the UI uses to gate the chat (forced reset on first login for backfilled
// accounts).

function _extractMessage(error: any, fallback: string): string {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail) && detail[0]?.msg) {
    return detail.map((d: any) => d?.msg).filter(Boolean).join('; ');
  }
  if (typeof detail === 'string') return detail;
  return error?.message || fallback;
}

export const loginStudent = async (
  username: string,
  password: string
): Promise<AuthResponse> => {
  try {
    const response = await API.post('/auth/login', { username, password });
    return response.data;
  } catch (error: any) {
    console.error('Student login error:', error.response?.data || error.message);
    throw new Error(_extractMessage(error, 'Login failed'));
  }
};

export const registerStudent = async (data: RegisterRequest): Promise<AuthResponse> => {
  try {
    const response = await API.post('/auth/register', data);
    return response.data;
  } catch (error: any) {
    console.error('Student registration error:', error.response?.data || error.message);
    throw new Error(_extractMessage(error, 'Registration failed'));
  }
};

export const resetStudentPassword = async (
  current_password: string,
  new_password: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await API.post('/auth/reset-password', {
      current_password,
      new_password,
    });
    return response.data;
  } catch (error: any) {
    console.error('Password reset error:', error.response?.data || error.message);
    throw new Error(_extractMessage(error, 'Could not reset password'));
  }
};

// Legacy alias: a few older callsites import `loginUser`; keep it as a thin
// shim so we don't have to chase every import site in this PR. New code
// should call `loginStudent` directly.
export const loginUser = async (
  username: string,
  password: string
): Promise<AuthResponse> => loginStudent(username, password);

// ===========================================================================
// Teacher auth: refresh-token plumbing
// ===========================================================================
//
// Token model (see backend/src/teachers/security.py):
//   - access token   — short-lived (2h), sent on every API request
//   - refresh token  — long-lived (90d), only used at /teachers/refresh
//
// localStorage keys (kept in sync with TeacherAuthContext.tsx):
//   teacherToken         — current access token
//   teacherRefreshToken  — current refresh token
//   teacher              — cached teacher profile JSON
//
// Flow on 401:
//   1. The response interceptor catches the 401 on a TEACHER endpoint.
//   2. If we have a refresh token, call /teachers/refresh ONCE (deduped).
//   3. On success: store the new access token, retry the original request.
//   4. On failure (refresh expired / invalid): clear localStorage, dispatch
//      a `teacher-session-expired` event so the React TeacherAuthContext
//      can reset its state, and let the original 401 propagate so the route
//      guard redirects to /teacher-login.

const TEACHER_TOKEN_KEY = 'teacherToken';
const TEACHER_REFRESH_TOKEN_KEY = 'teacherRefreshToken';
const TEACHER_PROFILE_KEY = 'teacher';

// Student session keys. Mirrors the teacher model: a short-lived access token +
// a long-lived refresh token, both in localStorage. The cached student profile
// stays under the pre-existing `user` key that AuthContext already manages.
const STUDENT_TOKEN_KEY = 'studentToken';
const STUDENT_REFRESH_TOKEN_KEY = 'studentRefreshToken';
const STUDENT_USER_KEY = 'user';

/** Dedupes parallel refresh attempts. If 5 requests 401 at once, only ONE
 *  /refresh call fires and the other 4 await this promise. */
let _refreshInFlight: Promise<string> | null = null;
/** Student equivalent of `_refreshInFlight` — separate so a teacher and a
 *  student session in the same browser never share a refresh promise. */
let _studentRefreshInFlight: Promise<string> | null = null;

function _getTeacherRefreshToken(): string | null {
  return localStorage.getItem(TEACHER_REFRESH_TOKEN_KEY);
}

function _clearTeacherSession() {
  localStorage.removeItem(TEACHER_TOKEN_KEY);
  localStorage.removeItem(TEACHER_REFRESH_TOKEN_KEY);
  localStorage.removeItem(TEACHER_PROFILE_KEY);
  // TeacherAuthContext listens for this so it can wipe React state too.
  window.dispatchEvent(new Event('teacher-session-expired'));
}

/** Try to mint a new access token from the stored refresh token.
 *  Returns the new access token on success; throws on failure. */
async function _refreshTeacherAccessToken(): Promise<string> {
  if (_refreshInFlight) return _refreshInFlight;

  const refresh = _getTeacherRefreshToken();
  if (!refresh) throw new Error('No refresh token available');

  _refreshInFlight = (async () => {
    try {
      // Use base axios (NOT the shared API instance) — avoids the request
      // interceptor that would re-attach the dead access token.
      const baseURL = (process.env.REACT_APP_BACKEND_BASE_URL || '') + '/api';
      const response = await axios.post(`${baseURL}/teachers/refresh`, {
        refresh_token: refresh,
      });
      const newAccess: string = response.data?.token;
      const newRefresh: string = response.data?.refresh_token ?? refresh;
      if (!newAccess) throw new Error('Refresh response missing token');
      localStorage.setItem(TEACHER_TOKEN_KEY, newAccess);
      localStorage.setItem(TEACHER_REFRESH_TOKEN_KEY, newRefresh);
      // TeacherAuthContext holds the token in React state; tell it so callers
      // that pass `token` explicitly don't keep sending the dead one.
      window.dispatchEvent(new CustomEvent('teacher-token-refreshed', { detail: newAccess }));
      return newAccess;
    } finally {
      _refreshInFlight = null;
    }
  })();
  return _refreshInFlight;
}

// --- Student session plumbing (mirrors the teacher helpers above) ----------

function _getStudentRefreshToken(): string | null {
  return localStorage.getItem(STUDENT_REFRESH_TOKEN_KEY);
}

/** Persist a fresh student session (called by AuthContext on login/register). */
export function setStudentSession(token: string, refreshToken: string): void {
  localStorage.setItem(STUDENT_TOKEN_KEY, token);
  localStorage.setItem(STUDENT_REFRESH_TOKEN_KEY, refreshToken);
}

/** Wipe the student session. Also clears the cached `user` profile and tells
 *  AuthContext (via event) so it can reset React state and bounce to /login. */
export function clearStudentSession(): void {
  localStorage.removeItem(STUDENT_TOKEN_KEY);
  localStorage.removeItem(STUDENT_REFRESH_TOKEN_KEY);
  localStorage.removeItem(STUDENT_USER_KEY);
  window.dispatchEvent(new Event('student-session-expired'));
}

/** Try to mint a new access token from the stored student refresh token.
 *  Returns the new access token on success; throws on failure. */
async function _refreshStudentAccessToken(): Promise<string> {
  if (_studentRefreshInFlight) return _studentRefreshInFlight;

  const refresh = _getStudentRefreshToken();
  if (!refresh) throw new Error('No refresh token available');

  _studentRefreshInFlight = (async () => {
    try {
      // Use base axios (NOT the shared API instance) — avoids the request
      // interceptor that would re-attach the dead access token.
      const baseURL = (process.env.REACT_APP_BACKEND_BASE_URL || '') + '/api';
      const response = await axios.post(`${baseURL}/auth/refresh`, {
        refresh_token: refresh,
      });
      const newAccess: string = response.data?.token;
      const newRefresh: string = response.data?.refresh_token ?? refresh;
      if (!newAccess) throw new Error('Refresh response missing token');
      localStorage.setItem(STUDENT_TOKEN_KEY, newAccess);
      localStorage.setItem(STUDENT_REFRESH_TOKEN_KEY, newRefresh);
      window.dispatchEvent(new CustomEvent('student-token-refreshed', { detail: newAccess }));
      return newAccess;
    } finally {
      _studentRefreshInFlight = null;
    }
  })();
  return _studentRefreshInFlight;
}

/** Authorization header value for student requests that can't go through the
 *  shared `API` instance (streaming fetch, multipart uploads). Same order as
 *  the request interceptor: the access JWT, else the legacy bare user.id for
 *  pre-JWT sessions. Sending ONLY the bare id got every chat message a 401 once
 *  STUDENT_ALLOW_LEGACY_ID_TOKEN was turned off in production. */
function _studentAuthHeader(): string | undefined {
  const studentToken = localStorage.getItem(STUDENT_TOKEN_KEY);
  if (studentToken) return `Bearer ${studentToken}`;
  const userJson = localStorage.getItem(STUDENT_USER_KEY);
  return userJson ? `Bearer ${JSON.parse(userJson).id}` : undefined;
}

/** Run a student request that bypasses the `API` interceptor; on a 401, renew
 *  the access token once (it lasts 2 hours) and retry. `send` gets the header
 *  value to use and reports the HTTP status of what it sent. */
async function _withStudentAuth<T extends { status: number }>(
  send: (authorization: string | undefined) => Promise<T>,
): Promise<T> {
  const first = await send(_studentAuthHeader());
  if (first.status !== 401 || !_getStudentRefreshToken()) return first;
  try {
    const fresh = await _refreshStudentAccessToken();
    return await send(`Bearer ${fresh}`);
  } catch {
    clearStudentSession();
    return first;
  }
}

/** Heuristic: is this request hitting a teacher endpoint? Used by the
 *  interceptor to route a 401 to the right refresh flow — teacher refresh when
 *  this returns true, student refresh when false (both flows now exist). */
function _isTeacherRequest(config: any): boolean {
  const url: string = config?.url || '';
  if (url.startsWith('/teachers')) return true;
  // Teacher endpoints called with absolute URL (shouldn't happen with our
  // axios setup, but be defensive).
  if (url.includes('/api/teachers')) return true;
  // The request was authorised via a Bearer header that matches the stored
  // teacher token — treat it as a teacher request even if the URL doesn't
  // start with /teachers (e.g. teacher hits /students, /classes etc.).
  const auth = config?.headers?.Authorization || config?.headers?.authorization;
  const teacherToken = localStorage.getItem(TEACHER_TOKEN_KEY);
  if (auth && teacherToken && typeof auth === 'string' && auth === `Bearer ${teacherToken}`) {
    return true;
  }
  // The header can also carry a STALE teacher token — a component captured it
  // before a silent refresh swapped localStorage, so it no longer matches
  // above. Recognise it by the JWT payload instead: teacher tokens carry
  // role="teacher", while the student bearer is a bare user id (not a JWT).
  if (typeof auth === 'string' && auth.startsWith('Bearer ')) {
    return _jwtRole(auth.slice('Bearer '.length)) === 'teacher';
  }
  return false;
}

/** Best-effort read of the `role` claim from a JWT. Returns null for
 *  anything that isn't a decodable JWT. No signature check — this only
 *  routes the 401 to the right refresh flow; the backend still verifies. */
function _jwtRole(token: string): string | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload?.role === 'string' ? payload.role : null;
  } catch {
    return null;
  }
}

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    if (status === 401 && original && !original._refreshRetried) {
      // Teacher endpoints with a live refresh token → silent teacher refresh.
      if (_isTeacherRequest(original) && _getTeacherRefreshToken()) {
        original._refreshRetried = true; // prevent infinite loops
        try {
          const newAccess = await _refreshTeacherAccessToken();
          // Re-attach the new token explicitly (the request interceptor will
          // also set it from localStorage, but being explicit avoids races).
          if (!original.headers) original.headers = {};
          original.headers.Authorization = `Bearer ${newAccess}`;
          return API(original);
        } catch (refreshErr) {
          _clearTeacherSession();
          return Promise.reject(error);
        }
      }
      // Any non-teacher request carrying a student refresh token → silent
      // student refresh. Legacy sessions (bare user.id, no refresh token) fall
      // through and the 401 surfaces to AuthContext as before.
      if (!_isTeacherRequest(original) && _getStudentRefreshToken()) {
        original._refreshRetried = true;
        try {
          const newAccess = await _refreshStudentAccessToken();
          if (!original.headers) original.headers = {};
          original.headers.Authorization = `Bearer ${newAccess}`;
          return API(original);
        } catch (refreshErr) {
          clearStudentSession();
          return Promise.reject(error);
        }
      }
    }
    return Promise.reject(error);
  },
);

// --- Teacher auth (username + password) ---

export const registerTeacher = async (data: TeacherRegisterRequest): Promise<TeacherAuthResponse> => {
  try {
    const response = await API.post('/teachers/register', data);
    return response.data;
  } catch (error: any) {
    console.error("Teacher registration error:", error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    const message = Array.isArray(detail)
      ? (detail[0]?.msg || 'Teacher registration failed')
      : (detail || 'Teacher registration failed');
    throw new Error(message);
  }
};

export const loginTeacher = async (username: string, password: string): Promise<TeacherAuthResponse> => {
  try {
    const response = await API.post('/teachers/login', { username, password });
    return response.data;
  } catch (error: any) {
    console.error("Teacher login error:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Teacher login failed');
  }
};

export const getTeacherMe = async (token: string): Promise<Teacher> => {
  const response = await API.get('/teachers/me', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

/** Set the teacher/facilitator's preferred "Ask the coach" reply language. */
export const updateTeacherLanguage = async (
  token: string,
  language: 'en' | 'hi'
): Promise<Teacher> => {
  try {
    const response = await API.patch('/teachers/me/language', { language }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Update teacher language error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to change language');
  }
};

export const addTeacherClass = async (
  token: string,
  cls: TeacherClassItem
): Promise<Teacher> => {
  try {
    const response = await API.post('/teachers/me/classes', cls, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Add teacher class error:', error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    const message = Array.isArray(detail)
      ? (detail[0]?.msg || 'Failed to add class')
      : (detail || 'Failed to add class');
    throw new Error(message);
  }
};

export const removeTeacherClass = async (
  token: string,
  cls: TeacherClassItem
): Promise<Teacher> => {
  try {
    const response = await API.delete('/teachers/me/classes', {
      headers: { Authorization: `Bearer ${token}` },
      data: cls,
    });
    return response.data;
  } catch (error: any) {
    console.error('Remove teacher class error:', error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    const message = Array.isArray(detail)
      ? (detail[0]?.msg || 'Failed to remove class')
      : (detail || 'Failed to remove class');
    throw new Error(message);
  }
};

/** Update the teacher's subject profile (primary + substitute subjects). */
export const updateTeacherSubjects = async (
  token: string,
  payload: { subjects: TeacherSubject[]; substitute_subjects: TeacherSubject[] }
): Promise<Teacher> => {
  try {
    const response = await API.put('/teachers/me/subjects', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Update teacher subjects error:', error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    const message = Array.isArray(detail)
      ? (detail[0]?.msg || 'Failed to save subjects')
      : (detail || 'Failed to save subjects');
    throw new Error(message);
  }
};

/** NCERT chapters for one of the teacher's grades, scoped to their subjects.
 *  Grades without an indexed curriculum come back with `subjects: []`. */
export const getTeacherCurriculum = async (
  token: string,
  grade: number
): Promise<TeacherCurriculumResponse> => {
  try {
    const response = await API.get<TeacherCurriculumResponse>('/teachers/me/curriculum', {
      params: { grade },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Get teacher curriculum error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch curriculum');
  }
};

/** Fetch the teacher's ticked-off syllabus sub-topics for a class. Only DONE
 *  items are returned; `completed_at` is always set on GET results. */
export const getSyllabusProgress = async (
  token: string,
  grade: number,
  section?: string | null
): Promise<{ items: SyllabusItem[] }> => {
  try {
    const params: Record<string, string | number> = { grade };
    if (section) params.section = section;
    const response = await API.get<{ items: SyllabusItem[] }>('/teachers/me/syllabus-progress', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Get syllabus progress error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch syllabus progress');
  }
};

/** Tick / untick a syllabus sub-topic. `done: true` timestamps it server-side;
 *  `done: false` deletes it (the returned item has `completed_at: null`). */
export const markSyllabusProgress = async (
  token: string,
  body: {
    grade: number;
    section?: string | null;
    subject?: string | null;
    chapter_number?: number | null;
    chapter_title: string;
    sub_topic: string;
    done: boolean;
  }
): Promise<{ item: SyllabusItem }> => {
  try {
    const response = await API.post<{ item: SyllabusItem }>(
      '/teachers/me/syllabus-progress',
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Mark syllabus progress error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to save syllabus progress');
  }
};

// --- Custom ("Global") syllabus: the teacher's own chapter list per class ---

export interface CustomSyllabusChapter {
  title: string;
  /** Subject this chapter belongs to (e.g. "Maths", "Physics"); null = ungrouped. */
  subject?: string | null;
  sub_topics: string[];
}

export interface CustomSyllabusResponse {
  name: string | null;
  source: 'pdf' | 'manual' | null;
  chapters: CustomSyllabusChapter[];
  updated_at: string | null;
}

/** The teacher's own ("Global") syllabus for a class. Never 404s for an owned
 *  class — an empty syllabus comes back with `chapters: []`. */
export const getCustomSyllabus = async (
  token: string,
  grade: number,
  section?: string | null
): Promise<CustomSyllabusResponse> => {
  try {
    const params: Record<string, string | number> = { grade };
    if (section) params.section = section;
    const response = await API.get<CustomSyllabusResponse>('/teachers/me/custom-syllabus', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Get custom syllabus error:', error.response?.data || error.message);
    throw new Error(_extractMessage(error, 'Failed to load your syllabus'));
  }
};

/** FULL-STATE upsert of the custom syllabus (empty `chapters` clears it). */
export const saveCustomSyllabus = async (
  token: string,
  body: {
    grade: number;
    section?: string | null;
    name?: string;
    source?: 'pdf' | 'manual';
    chapters: CustomSyllabusChapter[];
  }
): Promise<CustomSyllabusResponse> => {
  try {
    const response = await API.put<CustomSyllabusResponse>('/teachers/me/custom-syllabus', body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Save custom syllabus error:', error.response?.data || error.message);
    throw new Error(_extractMessage(error, 'Failed to save your syllabus'));
  }
};

/**
 * Extract a PROPOSED syllabus from an uploaded curriculum PDF. Nothing is
 * saved — the teacher previews the result, then PUTs via saveCustomSyllabus.
 *
 * IMPORTANT: like transcribeAudioAsTeacher, this must NOT use the shared `API`
 * instance — its `Content-Type: application/json` default makes axios
 * JSON-stringify the FormData (dropping the file → 422). Base axios with no
 * content-type lets the browser set `multipart/form-data` with the boundary.
 */
export const extractCurriculumPdf = async (
  token: string,
  file: File,
  grade: number,
  section?: string | null
): Promise<{ name: string; chapters: CustomSyllabusChapter[] }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('grade', String(grade));
    if (section) formData.append('section', section);

    const baseURL = (process.env.REACT_APP_BACKEND_BASE_URL || '') + '/api';
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    const response = await axios.post(`${baseURL}/teachers/me/custom-syllabus/extract-pdf`, formData, {
      headers,
    });
    return response.data;
  } catch (error: any) {
    console.error('Extract curriculum PDF error:', error.response?.data || error.message);
    // 400 (bad/oversized pdf) and 502 (extraction failed) carry a useful detail.
    throw new Error(_extractMessage(error, 'Could not read that PDF. Please try again.'));
  }
};

export interface CoachChatMessage {
  role: 'teacher' | 'coach';
  content: string;
  /** Set on a saved coach turn the server couldn't finish. Only ever read from
   *  history — turns sent to the server leave it off. */
  truncated?: boolean;
}

/** Ask-the-coach: send the running conversation for a class; get the coach's
 *  next reply (grounded server-side in the class's real data). Pass
 *  `student_id` to ground the chat in one student's report as well. */
export const askCoach = async (
  token: string,
  body: {
    grade: number;
    section?: string | null;
    messages: CoachChatMessage[];
    student_id?: number;
    /** Continue a saved chat; omit to start (and save) a new one. */
    chat_id?: number | null;
    /** Image the teacher uploaded this turn (vision) — from uploadCoachImage. */
    attached_image_id?: string | null;
  }
): Promise<{ reply: string; chat_id?: number | null; truncated?: boolean }> => {
  try {
    const response = await API.post<{ reply: string; chat_id?: number | null; truncated?: boolean }>(
      '/teachers/me/coach-chat', body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Ask coach error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || "The curiosity is unavailable right now. Please try again.");
  }
};

// --- Admin: facilitator-usage tracking (shared-secret admin login) ---

export interface FacilitatorClass {
  grade: number;
  section?: string | null;
  school?: string | null;   // multi-school facilitators; falls back to the facilitator's school
}

export interface FacilitatorUsage {
  id: number;
  name?: string | null;
  username: string;
  school: string;
  subjects: string[];
  classes: FacilitatorClass[];
  created_at?: string | null;
  last_login_at?: string | null;
  last_active?: string | null;
  coach_chats: number;
  topics_ticked: number;
  homeworks_set: number;
  exams_set: number;
  reinforcements: number;
  assessments: number;
  pdfs_uploaded: number;
  coach_minutes: number;
  lesson_plans: number;
  active_days: number;
  sessions_conducted: number;
  students_enrolled: number;
  attendance_pct: number | null;
  planned_visit_days: number[];
  missed_visits: number;
  checked_in_today: boolean;
  top_topics: string[];
  recent_chats: {
    title: string;
    updated_at?: string | null;
    messages: number;
    grade?: number | null;
    section?: string | null;
  }[];
}

export interface VisitDay {
  weekday: number; // 0=Mon .. 6=Sun
  label: string;
  date: string;
  planned: boolean;
  status: 'attended' | 'missed' | 'upcoming' | 'not_planned';
}

export const adminLogin = async (
  username: string,
  password: string
): Promise<{ token: string }> => {
  try {
    const res = await API.post<{ token: string }>('/monitor/login', { username, password });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Incorrect monitor username or password.');
  }
};

// Student figures are weekly averages, facilitator figures per-school-day
// averages, and every % is a ratio of summed totals — see the backend's
// `_avg_of_totals`. weeks_averaged / days_averaged say how many buckets went in.
export interface MonitorOutcomes {
  weeks_averaged: number;
  days_averaged: number;
  weeks_partial: boolean;   // window too short to hold a whole week
  days_partial: boolean;    // window too short to hold a finished day
  active_students: number;              // avg active students per week
  students_total: number;               // onboarded roster
  active_students_pct: number | null;   // weekly average, over the roster
  // Call 2026-08-26 — student engagement as TIME, not a chat count.
  student_minutes_total: number;
  student_active_days: number;            // (student x day) pairs behind the average
  student_minutes_per_day: number | null; // avg minutes on a day a child uses it
  // The same reading for facilitators — a programme-wide sum grew just by
  // onboarding more of them (call 2026-08-26).
  facilitator_minutes_total: number;
  facilitator_active_days: number;        // (facilitator x day) pairs
  facilitator_minutes_per_day: number | null;
  students_every_week: number;          // turned up in every week of the period
  students_any_week: number;
  students_every_week_pct: number | null;
  students_every_week_roster_pct: number | null;
  inside_module_students: number;       // asked about the STEM modules
  inside_module_pct: number | null;
  outside_module_students: number;      // asked about anything else
  outside_module_pct: number | null;
  exploratory_questioners: number;
  exploratory_pct: number | null;
  after_school_engagers: number;
  after_school_pct: number | null;
  take_home_prompts: number;
  take_home_students: number;
  take_home_pct: number | null;
  prompted_engagers: number;
  prompted_pct: number | null;
  facilitators_total: number;           // onboarded facilitators
  facilitators_daily: number;           // avg daily users per school day
  facilitators_daily_pct: number | null;
  under30_facilitators: number;         // facilitators averaging under 30 min/day
  under30_base: number;                 // ALL onboarded facilitators (zero usage included)
  under30_days_in_range: number;        // the divisor: days in the selected period
  under30_pct: number | null;
  // #174 rework — STEM curriculum delivered via CC, denominator is the module
  // list (Athira 2026-08-25 dropped the visit-schedule denominator entirely).
  modules_total: number;   // the denominator: the pilot's 15 yellow-marked modules
  modules_loaded: number;  // modules in the loaded curriculum, for context
  modules_covered: number; // of the 15, delivered to one of that module's own grades
  module_coverage_pct: number | null;
  modules_covered_names: string[];
  modules_untracked: number;         // pilot modules with NO content in CC (6 today)
  modules_untracked_names: string[]; // which ones, so the hint can name them
  modules_taught_any: number;        // modules taught through CC, pilot or not
  sessions_held: number;
  coverage_until: string | null;
}

export interface ActivityTimeline {
  points: { date: string; students: number; facilitator_logins: number }[];
}

export const getActivityTimeline = async (token: string, days = 30): Promise<ActivityTimeline> => {
  try {
    const res = await API.get<ActivityTimeline>(`/monitor/activity-timeline?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load activity timeline.');
  }
};

export interface OutcomeTrendPoint {
  date: string;
  active_students: number;
  exploratory_pct: number | null;
  inside_module_pct: number | null;
  outside_module_pct: number | null;
  facilitators_pct: number | null;
}

export interface OutcomesTrend {
  points: OutcomeTrendPoint[];
}

export const getOutcomesTrend = async (
  token: string,
  days = 90,
  window?: { since?: string; until?: string },
): Promise<OutcomesTrend> => {
  try {
    const qs = windowQs(window);
    const res = await API.get<OutcomesTrend>(
      `/monitor/outcomes-trend${qs ? `${qs}&` : '?'}days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load outcomes trend.');
  }
};

export interface ActivityBySchool {
  schools: {
    school: string;
    active_students: number;
    chats: number;
    minutes_total: number;
    // Minutes a child in this school spends on a day they use the coach.
    minutes_per_active_day: number | null;
  }[];
}

export const getActivityBySchool = async (
  token: string,
  window?: { since?: string; until?: string },
): Promise<ActivityBySchool> => {
  try {
    const res = await API.get<ActivityBySchool>(`/monitor/activity-by-school${windowQs(window)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load activity by school.');
  }
};

export interface ActivityByHour {
  hours: { hour: number; after_school: number; school: number }[];
  after_school_pct: number | null;
}

export const getActivityByHour = async (
  token: string,
  window?: { since?: string; until?: string },
): Promise<ActivityByHour> => {
  try {
    const res = await API.get<ActivityByHour>(`/monitor/activity-by-hour${windowQs(window)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load activity by hour.');
  }
};

export interface RecentProject {
  id: number;
  title: string;
  student_name?: string | null;
  school: string;
  grade: number;
  section?: string | null;
  shared_at: string;
  image_url?: string | null;
}

export interface ProjectsShared {
  window_count: number;
  vacation_count: number;
  vacation_label: string;
  chat_mentions: number;
  recent: RecentProject[];
}

export const getMonitorProjectsShared = async (
  token: string,
  window?: { since?: string; until?: string },
): Promise<ProjectsShared> => {
  try {
    const res = await API.get<ProjectsShared>(`/monitor/projects-shared${windowQs(window)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load projects shared.');
  }
};

export const getMonitorOutcomes = async (
  token: string,
  window?: { since?: string; until?: string },
): Promise<MonitorOutcomes> => {
  try {
    const res = await API.get<MonitorOutcomes>(`/monitor/outcomes${windowQs(window)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load outcomes.');
  }
};

export const getFacilitators = async (
  token: string,
  window?: { since?: string; until?: string },
): Promise<FacilitatorUsage[]> => {
  try {
    const params = new URLSearchParams();
    if (window?.since) params.set('since', window.since);
    if (window?.until) params.set('until', window.until);
    const qs = params.toString();
    const res = await API.get<{ facilitators: FacilitatorUsage[] }>(
      `/monitor/facilitators${qs ? `?${qs}` : ''}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data.facilitators || [];
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load facilitators.');
  }
};

export interface MostActiveStudent {
  id: number;
  name: string;
  school: string;
  grade: number;
  section?: string | null;
  /** Time on the coach in the period, and the days behind it — the list ranks on this. */
  minutes_total: number;
  active_days: number;
  chats: number;
  last_active?: string | null;
}

export interface StudentsOverview {
  total_students: number;
  total_student_chats: number;
  active_students_week: number;
  most_active_students: MostActiveStudent[];
}

const windowQs = (w?: { since?: string; until?: string }): string => {
  const p = new URLSearchParams();
  if (w?.since) p.set('since', w.since);
  if (w?.until) p.set('until', w.until);
  const qs = p.toString();
  return qs ? `?${qs}` : '';
};

export const getStudentsOverview = async (
  token: string,
  window?: { since?: string; until?: string },
): Promise<StudentsOverview> => {
  try {
    const res = await API.get<StudentsOverview>(`/monitor/students-overview${windowQs(window)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load student overview.');
  }
};

export interface SchoolSummary {
  school: string;
  total_students: number;
  active_students_week: number;
}

export const getSchoolSummary = async (token: string, school: string): Promise<SchoolSummary> => {
  try {
    const res = await API.get<SchoolSummary>(`/monitor/schools/${encodeURIComponent(school)}/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load school summary.');
  }
};

export interface FacilitatorLessonPlan {
  title: string;
  subject?: string | null;
  grade?: number | null;
  section?: string | null;
  school?: string | null;
  /** 'generated' (built in the Planner), 'draft' (saved), 'pushed' (sent to the class). */
  status: string;
  pushed_at?: string | null;
  updated_at?: string | null;
}

export interface FacilitatorChatMessage {
  role: string;
  content: string;
}

export interface FacilitatorChat {
  id: number;
  title: string;
  grade?: number | null;
  section?: string | null;
  updated_at?: string | null;
  message_count: number;
  // Empty on the M&E monitor — transcripts are opt-in server-side and only the
  // internal super-admin tool asks for them.
  messages: FacilitatorChatMessage[];
}

export interface FacilitatorCompletedTopic {
  sub_topic: string;
  chapter_title?: string | null;
  subject?: string | null;
  grade?: number | null;
  section?: string | null;
  completed_at?: string | null;
}

export interface FacilitatorDetail {
  id: number;
  name?: string | null;
  username: string;
  school: string;
  subjects: string[];
  classes: FacilitatorClass[];
  last_login_at?: string | null;
  last_active?: string | null;
  coach_chats: number;
  topics_ticked: number;
  homeworks_set: number;
  exams_set: number;
  reinforcements: number;
  assessments: number;
  pdfs_uploaded: number;
  coach_minutes: number;
  active_days: number;
  sessions_conducted: number;
  students_enrolled: number;
  attendance_pct: number | null;
  session_attendance: { session_date: string; present: number; absent: number; present_names: string[]; absent_names: string[]; school?: string | null }[];
  class_attendance: { school: string; grade: number; section: string | null; label: string; enrolled: number; attendance_pct: number | null }[];
  // Per school — what the school cards show, computed exactly as the
  // school-scoped page computes it so the two can never disagree.
  school_attendance: { school: string; enrolled: number; sessions: number; attendance_pct: number | null }[];
  sessions_by_class: { school: string; grade: number; section: string | null; label: string; sessions: number; dates: string[] }[];
  planned_visit_days: number[];
  visit_days: VisitDay[];
  visits_planned: number;
  visits_missed: number;
  checked_in_today: boolean;
  completed_topics: FacilitatorCompletedTopic[];
  lesson_plans: FacilitatorLessonPlan[];
  /** Plans built in the period — the list above is capped for display. */
  lesson_plans_built: number;
  chats: FacilitatorChat[];
  scope_school: string | null;   // set when the page is scoped to one school
  schools: string[];             // every school this facilitator works in
}

export const getFacilitatorDetail = async (
  token: string,
  id: number,
  window?: { since?: string; until?: string },
  // One of the facilitator's schools. Attendance and the other school-specific
  // figures are only meaningful inside a school (call 2026-08-26).
  school?: string,
): Promise<FacilitatorDetail> => {
  try {
    const qs = windowQs(window);
    const schoolQs = school ? `${qs ? '&' : '?'}school=${encodeURIComponent(school)}` : '';
    const res = await API.get<FacilitatorDetail>(
      `/monitor/facilitators/${id}${qs}${schoolQs}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load facilitator.');
  }
};

// --- Super-admin "Mission Control" master dashboard (tasks 147/148) ---

export interface SAObjectiveTile {
  key: string;
  label: string;
  value?: number | null;
  total?: number | null;
  target?: number | null;
  unit: 'count' | 'percent';
  state: 'good' | 'warn' | 'crit' | 'neutral' | 'dark';
  note?: string | null;
}

export interface SAInterventionItem {
  sig: 'crit' | 'warn';
  title: string;
  detail?: string | null;
  module: string;
  facilitator_id?: number | null;
}

export interface SuperAdminOverview {
  week_label: string;
  week_offset: number;
  is_current_week: boolean;
  tiles: SAObjectiveTile[];
  interventions: SAInterventionItem[];
}

/** Central (super-admin) shared-secret login — separate from the monitor login. */
export const superAdminLogin = async (username: string, password: string): Promise<{ token: string }> => {
  try {
    const res = await API.post<{ token: string }>('/super-admin/login', { username, password });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Incorrect username or password.');
  }
};

export const getSuperAdminOverview = async (token: string, weekOffset = 0): Promise<SuperAdminOverview> => {
  try {
    const res = await API.get<SuperAdminOverview>(`/super-admin/overview?week_offset=${weekOffset}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load overview.');
  }
};

export interface SASchoolRow {
  school: string;
  students: number;
  active_week: number;
  facilitators: number;
  // Understanding mix across the school's roster (AI-assessed); sums to <= students.
  got: number;
  almost: number;
  needs: number;
  no_data: number;
}

export interface SAClassRow {
  grade: number;
  section?: string | null;
  label: string;
  students: number;
  active_week: number;
  got: number;
  almost: number;
  needs: number;
  no_data: number;
}

export interface SAStudentRow {
  student_id: number;
  name: string;
  roll?: number | null;
  bucket: string;
  category: string;
  struggled: number;
  curiosity?: number | null;
}

export interface SAClassDrill {
  school: string;
  grade: number;
  section?: string | null;
  label: string;
  size: number;
  students: SAStudentRow[];
}

export interface SAStudentTopic {
  topic: string;
  subject?: string | null;
  state: string;
  struggled: boolean;
}

export interface SAStudentProfile {
  student_id: number;
  name: string;
  school: string;
  grade: number;
  section?: string | null;
  roll?: number | null;
  bucket: string;
  category: string;
  curiosity?: number | null;
  quiz?: number | null;
  topics: SAStudentTopic[];
}

const saHeaders = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export const getSuperAdminSchools = async (token: string): Promise<SASchoolRow[]> => {
  try {
    const res = await API.get<SASchoolRow[]>('/super-admin/schools', saHeaders(token));
    return res.data || [];
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load schools.');
  }
};

export const getSuperAdminSchoolClasses = async (token: string, school: string): Promise<SAClassRow[]> => {
  try {
    const res = await API.get<SAClassRow[]>(`/super-admin/schools/${encodeURIComponent(school)}/classes`, saHeaders(token));
    return res.data || [];
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load classes.');
  }
};

export const getSuperAdminClass = async (
  token: string,
  school: string,
  grade: number,
  section?: string | null
): Promise<SAClassDrill> => {
  try {
    const params = new URLSearchParams({ school, grade: String(grade) });
    if (section) params.set('section', section);
    const res = await API.get<SAClassDrill>(`/super-admin/class?${params.toString()}`, saHeaders(token));
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load class.');
  }
};

export const getSuperAdminStudent = async (token: string, id: number): Promise<SAStudentProfile> => {
  try {
    const res = await API.get<SAStudentProfile>(`/super-admin/students/${id}`, saHeaders(token));
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load student.');
  }
};

export interface SAGroupTier { key: string; label: string; count: number; pct: number; note: string; }
export interface SAGroupWatch { student_id: number; name: string; detail: string; }
export interface SAStudentGroups {
  total_students: number;
  categorised: number;
  tiers: SAGroupTier[];
  watch: SAGroupWatch[];
  movement_available: boolean;
}

export const getSuperAdminStudentGroups = async (token: string): Promise<SAStudentGroups> => {
  try {
    const res = await API.get<SAStudentGroups>('/super-admin/student-groups', saHeaders(token));
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load student groups.');
  }
};

// Facilitators lens on the super-admin surface — same shape as the monitor's,
// but served (and gated) under /super-admin so the central token works.
export const getSuperAdminFacilitators = async (token: string): Promise<FacilitatorUsage[]> => {
  try {
    const res = await API.get<{ facilitators: FacilitatorUsage[] }>('/super-admin/facilitators', saHeaders(token));
    return res.data.facilitators || [];
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load facilitators.');
  }
};

export const getSuperAdminFacilitatorDetail = async (token: string, id: number): Promise<FacilitatorDetail> => {
  try {
    const res = await API.get<FacilitatorDetail>(`/super-admin/facilitators/${id}`, saHeaders(token));
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load facilitator.');
  }
};

export interface SAIVSignal {
  key: string;
  label: string;
  value?: string | null;
  detail?: string | null;
  sample: number;
  state: string; // 'live' | 'pending'
  dist: { label: string; count: number }[];
}
export interface SAInternalValidation {
  conversations_evaluated: number;
  signals: SAIVSignal[];
}

export const getSuperAdminInternalValidation = async (token: string): Promise<SAInternalValidation> => {
  try {
    const res = await API.get<SAInternalValidation>('/super-admin/internal-validation', saHeaders(token));
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load internal validation.');
  }
};

// --- M&E: "% students accessing the app during vacation" (PROVISIONAL) -------
// Per-school vacation windows, owned by the monitoring admin. A student counts
// as active if they used the coach on >=1 day inside their school's window.
export interface SchoolVacation {
  school: string;
  total_students: number;
  start_date?: string | null;   // YYYY-MM-DD, null until a window is set
  end_date?: string | null;     // YYYY-MM-DD
  label?: string | null;
  active_students?: number | null;
  pct?: number | null;          // % active on the coach (soft proxy), null until a window is set
  projects_shared?: number | null;        // # projects shared in the window (real signal)
  students_with_project?: number | null;   // distinct students who shared >=1 project
  project_pct?: number | null;             // % of students who shared >=1 project
}

export interface VacationOverall {
  schools_total: number;
  schools_with_window: number;
  total_students: number;       // denominator: students in schools with a window
  active_students: number;
  pct?: number | null;          // null until at least one school has a window
  projects_shared: number;
  students_with_project: number;
  project_pct?: number | null;
}

export interface VacationResponse {
  overall: VacationOverall;
  schools: SchoolVacation[];
}

/** Per-school vacation metric + windows for the monitor dashboard. */
export const getVacationMetric = async (token: string): Promise<VacationResponse> => {
  try {
    const res = await API.get<VacationResponse>('/monitor/vacation', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load vacation metric.');
  }
};

/** Set (or replace) one school's vacation window (dates are YYYY-MM-DD). */
export const setSchoolVacation = async (
  token: string,
  school: string,
  startDate: string,
  endDate: string,
  label?: string
): Promise<SchoolVacation> => {
  try {
    const res = await API.put<SchoolVacation>(
      '/monitor/vacation',
      { school, start_date: startDate, end_date: endDate, label: label || null },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to save vacation window.');
  }
};

/** Clear a school's vacation window. */
export const clearSchoolVacation = async (token: string, school: string): Promise<void> => {
  try {
    await API.delete('/monitor/vacation', {
      headers: { Authorization: `Bearer ${token}` },
      params: { school },
    });
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to clear vacation window.');
  }
};

/** Set which weekdays a facilitator is planned to visit (Mon=0 .. Sun=6). */
export const setVisitSchedule = async (
  token: string,
  id: number,
  plannedWeekdays: number[]
): Promise<{ teacher_id: number; planned_weekdays: number[] }> => {
  try {
    const res = await API.put<{ teacher_id: number; planned_weekdays: number[] }>(
      `/monitor/facilitators/${id}/visit-schedule`,
      { planned_weekdays: plannedWeekdays },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to save visit schedule.');
  }
};

/** Upload an image for the teacher coach chat (vision). Returns an `img_*` id +
 *  url. Uses plain axios (multipart) with the teacher token. */
export const uploadCoachImage = async (
  token: string,
  file: File | Blob
): Promise<{ image_id: string; url: string }> => {
  const baseURL = (process.env.REACT_APP_BACKEND_BASE_URL || '') + '/api';
  const formData = new FormData();
  formData.append('image', file, 'upload.jpg');
  const response = await axios.post(`${baseURL}/teachers/me/coach-chat/upload`, formData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export interface TeacherChatSummary {
  id: number;
  title?: string | null;
  grade: number;
  section?: string | null;
  updated_at?: string | null;
  message_count: number;
}

export const listCoachChats = async (
  token: string,
  grade?: number,
  section?: string | null
): Promise<TeacherChatSummary[]> => {
  try {
    const params: Record<string, string | number> = {};
    if (grade != null) params.grade = grade;
    if (section) params.section = section;
    const response = await API.get<TeacherChatSummary[]>('/teachers/me/coach-chats', {
      params, headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('List coach chats error:', error.response?.data || error.message);
    return [];
  }
};

export const getCoachChat = async (
  token: string,
  chatId: number
): Promise<{ id: number; title?: string | null; messages: CoachChatMessage[] }> => {
  try {
    const response = await API.get<{ id: number; title?: string | null; messages: CoachChatMessage[] }>(
      `/teachers/me/coach-chats/${chatId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Get coach chat error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to load that chat.');
  }
};

export const deleteCoachChat = async (token: string, chatId: number): Promise<void> => {
  try {
    await API.delete(`/teachers/me/coach-chats/${chatId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error: any) {
    console.error('Delete coach chat error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to delete that chat.');
  }
};

// ── "Mind Jog" lesson planner ────────────────────────────────────────────────
export interface LessonTopic {
  title: string;
  subject?: string | null;
  source?: string;
  stem_kb_document_id?: number | null;
}
export interface LessonActivity {
  id?: string;
  title: string;
  summary?: string;
}
export interface LessonPlanStep {
  title: string;
  detail: string;
}
export interface LessonPlanBody {
  intro: string;
  materials: string[];
  duration_min: number;
  steps: LessonPlanStep[];
  how_it_works: string;
  result_use: string;
}
export interface LessonPlanResponse {
  grade: number;
  section?: string | null;
  topic?: LessonTopic | null;
  activities: LessonActivity[];
  plan?: LessonPlanBody | null;
  status: string;
  pushed_at?: string | null;
  updated_at?: string | null;
}
export interface LessonTopicGroup {
  label: string;
  source: string;
  topics: LessonTopic[];
}
export interface LessonPlanHistoryItem {
  id: number;
  topic?: LessonTopic | null;
  activities: LessonActivity[];
  plan: LessonPlanBody;
  created_at: string;
}

const authHeader = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export const getLessonTopics = async (token: string, grade: number): Promise<LessonTopicGroup[]> => {
  try {
    const res = await API.get<{ groups: LessonTopicGroup[] }>(
      '/teachers/me/lesson-plan/topics', { params: { grade }, ...authHeader(token) });
    return res.data.groups || [];
  } catch (error: any) {
    console.error('Lesson topics error:', error.response?.data || error.message);
    return [];
  }
};

export const generateLessonActivities = async (
  token: string, body: { grade: number; section?: string | null; topic: LessonTopic }
): Promise<LessonActivity[]> => {
  try {
    const res = await API.post<{ activities: LessonActivity[] }>(
      '/teachers/me/lesson-plan/activities', body, authHeader(token));
    return res.data.activities || [];
  } catch (error: any) {
    console.error('Lesson activities error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || "Couldn't suggest activities right now.");
  }
};

export const generateLessonPlan = async (
  token: string,
  body: { grade: number; section?: string | null; topic: LessonTopic; activities: LessonActivity[] }
): Promise<LessonPlanResponse> => {
  try {
    const res = await API.post<LessonPlanResponse>(
      '/teachers/me/lesson-plan/generate', body, authHeader(token));
    return res.data;
  } catch (error: any) {
    console.error('Lesson plan generate error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || "Couldn't build the plan right now.");
  }
};

export const getLessonPlan = async (
  token: string, grade: number, section?: string | null
): Promise<LessonPlanResponse | null> => {
  try {
    const params: Record<string, string | number> = { grade };
    if (section) params.section = section;
    const res = await API.get<LessonPlanResponse>('/teachers/me/lesson-plan', { params, ...authHeader(token) });
    return res.data;
  } catch (error: any) {
    console.error('Get lesson plan error:', error.response?.data || error.message);
    return null;
  }
};

export const getLessonPlanHistory = async (
  token: string, grade: number, section?: string | null
): Promise<LessonPlanHistoryItem[]> => {
  try {
    const params: Record<string, string | number> = { grade };
    if (section) params.section = section;
    const res = await API.get<{ items: LessonPlanHistoryItem[] }>(
      '/teachers/me/lesson-plan/history', { params, ...authHeader(token) });
    return res.data.items || [];
  } catch (error: any) {
    console.error('Get lesson plan history error:', error.response?.data || error.message);
    return [];
  }
};

export const saveLessonPlan = async (
  token: string,
  body: { grade: number; section?: string | null; topic: LessonTopic; activities: LessonActivity[]; plan: LessonPlanBody }
): Promise<LessonPlanResponse> => {
  const res = await API.put<LessonPlanResponse>('/teachers/me/lesson-plan', body, authHeader(token));
  return res.data;
};

export const pushLessonPlan = async (
  token: string,
  body: { grade: number; section?: string | null; topic: LessonTopic; activities: LessonActivity[]; plan: LessonPlanBody }
): Promise<LessonPlanResponse> => {
  const res = await API.post<LessonPlanResponse>('/teachers/me/lesson-plan/push', body, authHeader(token));
  return res.data;
};

export interface VoiceTopicMatchResponse {
  candidates: VoiceTopicCandidate[];
  /** Part of the transcript that didn't match any chapter. */
  leftover: string;
  fallback_used: boolean;
}

/** Match a spoken description ("we did photosynthesis today…") against the
 *  grade's NCERT chapters. */
export const matchVoiceTopics = async (
  token: string,
  payload: { transcript: string; grade: number; section?: string | null }
): Promise<VoiceTopicMatchResponse> => {
  try {
    const body: { grade: number; section?: string; transcript: string } = {
      grade: payload.grade,
      transcript: payload.transcript,
    };
    if (payload.section) body.section = payload.section;
    const response = await API.post<VoiceTopicMatchResponse>('/teachers/me/match-topics', body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Match voice topics error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to match topics');
  }
};

/**
 * Send a recorded audio clip to the backend for speech-to-text.
 * If `language` (an ISO-639-1 code like "hi", "te", "en") is given, the model
 * is locked to that language; otherwise the speaker's saved preference is used,
 * and failing that the language is auto-detected.
 *
 * Measured note: on gpt-4o-transcribe the hint changes nothing — it read
 * English, Hindi and Hindi-English mixed speech correctly with and without it.
 * It matters as a fallback for whisper-1, which TRANSLATED a mixed clip into
 * English on auto-detect but transcribed it correctly when told `hi`.
 */
function savedSpeakerLanguage(key: 'user' | 'teacher'): string | undefined {
  // Read only the caller's own session: a stale student `user` left in the
  // same browser used to override the teacher's language on teacher STT.
  try {
    const raw = localStorage.getItem(key);
    const lang = raw ? JSON.parse(raw)?.language : undefined;
    return typeof lang === 'string' && lang.trim() ? lang.trim() : undefined;
  } catch {
    return undefined;  // corrupt localStorage must never break the mic
  }
}

export const transcribeAudio = async (
  audio: Blob,
  filename = 'recording.webm',
  language?: string
): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('audio', audio, filename);
    const lang = language ?? savedSpeakerLanguage('user');
    if (lang) {
      formData.append('language', lang);
    }

    // IMPORTANT: do NOT use the shared `API` instance here. Its default
    // `Content-Type: application/json` makes axios JSON-stringify the FormData
    // (dropping the file → 422). Use base axios with no content-type so the
    // browser sets `multipart/form-data` with the correct boundary itself.
    const baseURL = (process.env.REACT_APP_BACKEND_BASE_URL || '') + '/api';
    const headers: Record<string, string> = {};
    // Mirror the shared API interceptor: prefer the student ACCESS JWT, and fall
    // back to the legacy bare `user.id` only for sessions predating the JWT
    // rollout. This used to send the bare id ONLY, so the call survived purely
    // on the legacy path and would have died the day
    // STUDENT_ALLOW_LEGACY_ID_TOKEN is turned off.
    const studentToken = localStorage.getItem(STUDENT_TOKEN_KEY);
    const userJson = localStorage.getItem('user');
    if (studentToken) {
      headers.Authorization = `Bearer ${studentToken}`;
    } else if (userJson) {
      headers.Authorization = `Bearer ${JSON.parse(userJson).id}`;
    }
    const response = await axios.post(`${baseURL}/speech/transcribe`, formData, { headers });
    return response.data?.text ?? '';
  } catch (error: any) {
    console.error('Transcription error:', error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    let message = 'Could not transcribe audio. Please try again.';
    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail) && detail[0]?.msg) {
      // FastAPI validation errors come back as an array of objects.
      message = detail.map((d: any) => d?.msg).filter(Boolean).join('; ') || message;
    } else if (error.message) {
      message = error.message;
    }
    throw new Error(message);
  }
};

/**
 * Fetch the read-aloud audio for an AI reply as an MP3 blob.
 *
 * The backend generates the audio on first request and caches it, so the
 * first call for a message can take a few seconds; replays are instant.
 * Student-authed only — the listen button lives in the student chat.
 */
export const fetchMessageAudio = async (messageId: number): Promise<Blob> => {
  const baseURL = (process.env.REACT_APP_BACKEND_BASE_URL || '') + '/api';
  // Same auth dance as transcribeAudio: prefer the student ACCESS JWT, fall
  // back to the legacy bare user.id for pre-JWT sessions.
  const headers: Record<string, string> = {};
  const studentToken = localStorage.getItem(STUDENT_TOKEN_KEY);
  const userJson = localStorage.getItem('user');
  if (studentToken) {
    headers.Authorization = `Bearer ${studentToken}`;
  } else if (userJson) {
    headers.Authorization = `Bearer ${JSON.parse(userJson).id}`;
  }
  const response = await axios.get(`${baseURL}/speech/tts/${messageId}`, {
    headers,
    responseType: 'blob',
  });
  return response.data as Blob;
};

/**
 * Teacher-authed variant of `transcribeAudio` — same endpoint, but sends the
 * teacher Bearer JWT from localStorage instead of the student user id.
 *
 * IMPORTANT: like the original, this must NOT use the shared `API` instance —
 * its `Content-Type: application/json` default makes axios JSON-stringify the
 * FormData (dropping the file → 422). Base axios with no content-type lets the
 * browser set `multipart/form-data` with the correct boundary itself.
 */
export const transcribeAudioAsTeacher = async (
  audio: Blob,
  filename = 'recording.webm',
  language?: string
): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('audio', audio, filename);
    const lang = language ?? savedSpeakerLanguage('teacher');
    if (lang) {
      formData.append('language', lang);
    }

    const baseURL = (process.env.REACT_APP_BACKEND_BASE_URL || '') + '/api';
    const headers: Record<string, string> = {};
    const teacherToken = localStorage.getItem('teacherToken');
    if (teacherToken) {
      headers.Authorization = `Bearer ${teacherToken}`;
    }
    const response = await axios.post(`${baseURL}/speech/transcribe`, formData, { headers });
    return response.data?.text ?? '';
  } catch (error: any) {
    console.error('Transcription error:', error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    let message = 'Could not transcribe audio. Please try again.';
    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail) && detail[0]?.msg) {
      message = detail.map((d: any) => d?.msg).filter(Boolean).join('; ') || message;
    } else if (error.message) {
      message = error.message;
    }
    throw new Error(message);
  }
};

export const getStudentOptions = async (): Promise<StudentOptions> => {
  try {
    const response = await API.get('/config/student-options');
    return response.data;
  } catch (error: any) {
    console.error("Error fetching student options:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to get student options');
  }
};

export const refreshClassMetrics = async (
  school: string,
  grade: number,
  section?: string | null,
  includeHourly = true,
  startDate?: string | null,
  endDate?: string | null
): Promise<MetricsRefreshResponse> => {
  try {
    const params: Record<string, string> = {};
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    const payload = {
      school,
      grade,
      section: section ?? undefined,
      include_hourly: includeHourly,
    };

    const response = await API.post<MetricsRefreshResponse>('/analytics/refresh', payload, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error refreshing class metrics:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to refresh metrics');
  }
};

export const getClassDashboardMetrics = async (
  school: string,
  grade: number,
  section?: string | null,
  startDate?: string | null,
  endDate?: string | null
): Promise<DashboardResponse> => {
  try {
    const params: Record<string, string | number> = {
      school,
      grade,
    };
    if (section) {
      params.section = section;
    }
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await API.get<DashboardResponse>('/analytics/dashboard', { params });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching dashboard metrics:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch dashboard metrics');
  }
};

export const getStudentsForClass = async (
  school: string,
  grade: number,
  section?: string | null,
  tags?: string[],
  tagMode?: 'any' | 'all'
): Promise<StudentWithConversation[]> => {
  try {
    const params: Record<string, string | number> = {
      school,
      grade,
    };
    if (section) {
      params.section = section;
    }
    if (tags && tags.length > 0) {
      params.tags = tags.join(',');
    }
    if (tagMode) {
      params.tag_mode = tagMode;
    }
    // Pass the TEACHER token explicitly. /students now requires a teacher JWT
    // (it used to require nothing), and the request interceptor would otherwise
    // attach the student bearer, which this endpoint rejects. The 401-refresh
    // helper already recognises "teacher hits /students" by the header, so a
    // silent refresh still works.
    const teacherToken = localStorage.getItem(TEACHER_TOKEN_KEY);
    const response = await API.get<StudentWithConversation[]>('/students', {
      params,
      ...(teacherToken
        ? { headers: { Authorization: `Bearer ${teacherToken}` } }
        : {}),
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching students for class:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch students');
  }
};

export const updateStudentTags = async (studentId: number, tags: string[]): Promise<Student> => {
  try {
    const response = await API.patch<Student>(`/students/${studentId}`, { tags });
    return response.data;
  } catch (error: any) {
    console.error(`Error updating tags for student ${studentId}:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to update tags');
  }
};

export const getClassTags = async (
  school: string,
  grade: number,
  section?: string | null,
  query?: string | null
): Promise<string[]> => {
  try {
    const params: Record<string, string | number> = {
      school,
      grade,
    };
    if (section) {
      params.section = section;
    }
    if (query) {
      params.q = query;
    }
    const response = await API.get<string[]>('/students/tags', { params });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching class tags:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch tags');
  }
};

export const getConversationTags = async (query?: string | null): Promise<string[]> => {
  try {
    const params: Record<string, string> = {};
    if (query) {
      params.q = query;
    }
    const response = await API.get<string[]>('/conversations/tags', { params });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching conversation tags:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch conversation tags');
  }
};

export const getClassConversationTags = async (
  school: string,
  grade: number,
  section?: string | null,
  query?: string | null
): Promise<string[]> => {
  try {
    const params: Record<string, string | number> = {
      school,
      grade,
    };
    if (section) {
      params.section = section;
    }
    if (query) {
      params.q = query;
    }
    const response = await API.get<string[]>('/students/conversation-tags', { params });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching class conversation tags:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch conversation tags');
  }
};

export const updateConversationTags = async (
  conversationId: number,
  tags: string[]
): Promise<ConversationTagsResponse> => {
  try {
    const response = await API.patch<ConversationTagsResponse>(`/conversations/${conversationId}/tags`, { tags });
    return response.data;
  } catch (error: any) {
    console.error(`Error updating tags for conversation ${conversationId}:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to update conversation tags');
  }
};

export const updateStudentConversationTags = async (
  studentId: number,
  conversationId: number,
  tags: string[]
): Promise<ConversationTagsResponse> => {
  try {
    const response = await API.patch<ConversationTagsResponse>(
      `/students/${studentId}/conversations/${conversationId}/tags`,
      { tags }
    );
    return response.data;
  } catch (error: any) {
    console.error(
      `Error updating tags for conversation ${conversationId} (student ${studentId}):`,
      error.response?.data || error.message
    );
    throw new Error(error.response?.data?.detail || 'Failed to update conversation tags');
  }
};

export const getStudentDailyMetrics = async (
  school: string,
  grade: number,
  studentIds: number[],
  section?: string | null,
  startDate?: string | null,
  endDate?: string | null
): Promise<StudentDailyMetricsResponse> => {
  if (!studentIds || studentIds.length === 0) {
    throw new Error('At least one student ID is required');
  }

  try {
    const params: Record<string, string | number | (string | number)[]> = {
      school,
      grade,
      student_ids: studentIds,
    };
    if (section) {
      params.section = section;
    }
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    const response = await API.get<StudentDailyMetricsResponse>('/analytics/student-daily', {
      params,
      paramsSerializer: (queryParams) => {
        const searchParams = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            return;
          }
          if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(key, String(item)));
          } else {
            searchParams.append(key, String(value));
          }
        });
        return searchParams.toString();
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching student daily metrics:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch student daily metrics');
  }
};

export const getStudentConversations = async (
  studentId: number,
  limit = 3,
  offset = 0,
  day?: string | null,
  tags?: string[],
  tagMode?: 'any' | 'all'
): Promise<PaginatedStudentConversations> => {
  try {
    const params: Record<string, string | number> = {
      limit,
      offset,
    };
    if (day) {
      params.day = day;
    }
    if (tags && tags.length > 0) {
      params.tags = tags.join(',');
    }
    if (tagMode) {
      params.tag_mode = tagMode;
    }
    const response = await API.get<PaginatedStudentConversations>(`/students/${studentId}/conversations`, {
      params,
    });
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching conversations for student ${studentId}:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch conversations');
  }
};

export const getAllStudentConversations = async (
  studentId: number
): Promise<ConversationWithMessages[]> => {
  try {
    const response = await API.get<ConversationWithMessages[]>(`/students/${studentId}/conversations/all`);
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching all conversations for student ${studentId}:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch all conversations');
  }
};

export const getUserPersona = async (userId: number): Promise<UserPersona | null> => {
  try {
    const response = await API.get<UserPersona>(`/internal/users/${userId}/persona`);
    const persona = response.data;
    
    // Parse persona_data if it's a string
    if (persona && typeof persona.persona_data === 'string') {
      persona.persona_data = JSON.parse(persona.persona_data);
    }
    
    return persona;
  } catch (error: any) {
    if (error.response?.status === 404) {
      // No persona found - this is expected for users without personas
      return null;
    }
    console.error(`Error fetching persona for user ${userId}:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch user persona');
  }
};

export interface ClassAnalysisResponse {
  analysis?: string | null;
  status: AnalysisStatus;
  job_id?: string | null;
  computed_at?: string | null;
}

export interface AnalysisJobStatusResponse {
  job_id: string;
  status: JobStatus;
  analysis?: string | null;
  computed_at?: string | null;
  error_message?: string | null;
  analysis_status?: AnalysisStatus | null;
}

export const analyzeClassConversations = async (
  school: string,
  grade: number,
  section?: string | null,
  forceRefresh = false
): Promise<ClassAnalysisResponse> => {
  try {
    const params: Record<string, string | number | boolean> = {
      school,
      grade,
    };
    if (section) {
      params.section = section;
    }
    if (forceRefresh) {
      params.force_refresh = true;
    }
    const response = await API.post<ClassAnalysisResponse>('/students/class-analysis', {}, { params });
    return response.data;
  } catch (error: any) {
    console.error("Error analyzing class conversations:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to analyze class conversations');
  }
};

// --- Teacher reinforcement report (private, teacher-auth) ---

export interface ReinforcementTopic {
  topic: string;
  /** 'maths' | 'science' | 'social' | 'global' (teacher's own syllabus) | null. */
  subject?: string | null;
  /** True when this topic came from a pushed/syllabus curriculum conversation
   *  (vs. one the student explored on their own). Undefined on old cached reports. */
  from_curriculum?: boolean;
  n_students: number;
  struggled: number;
  breakdown: { never_learned: number; forgotten: number; partly_solid: number; solid: number };
  avg_depth?: number | null;
  class_grade: number;
  flagged: boolean;
  students_struggling: string[];
}

export interface ReinforcementReportData {
  class: { school: string; grade: number; section?: string | null; size: number; students_with_data: number };
  topics: ReinforcementTopic[];
  /** Per-student rows (Teacher Dashboard v2). Undefined on old cached reports. */
  students?: ReinforcementStudent[];
}

export interface ClassReinforcementResponse {
  report_data?: ReinforcementReportData | null;
  report_text?: string | null;
  status: string;
  computed_at?: string | null;
}

export const getClassReinforcementReport = async (
  token: string,
  grade: number,
  section?: string | null,
  forceRefresh = false,
  startDate?: string | null,
  endDate?: string | null,
  // Which of the teacher's schools this class is in. A facilitator can hold the
  // same grade/section in two schools; without this the backend picks whichever
  // class it finds first, so the dashboard for school B showed school A's
  // students (facilitator report, 2026-10-14).
  school?: string | null
): Promise<ClassReinforcementResponse> => {
  try {
    const params: Record<string, string | number | boolean> = { grade };
    if (section) params.section = section;
    if (school) params.school = school;
    if (forceRefresh) params.force_refresh = true;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await API.post<ClassReinforcementResponse>(
      '/students/class-reinforcement', {},
      { params, headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error fetching reinforcement report:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch reinforcement report');
  }
};

// --- Teacher reinforcement flags (private, teacher-auth) ---
// A flag tells the coach to gently steer one student toward a topic next session.

export interface ReinforcementFlag {
  id: number;
  student_id: number;
  student_name?: string | null;
  topic: string;
  subject?: string | null;
  note?: string | null;
  created_at?: string | null;
  /** 'active' = the coach is still steering the student toward it.
   *  'done'   = the student passed the end-of-topic quiz on it. */
  status?: 'active' | 'done';
  completed_at?: string | null;
  completion_quiz_score?: number | null;
}

export interface CreateReinforcementFlagsBody {
  grade: number;
  section?: string | null;
  student_ids: number[];
  topic: string;
  subject?: string | null;
  note?: string | null;
}

/** One syllabus topic a student finished with the coach (passed the quiz). */
export interface StudentTopicCompletion {
  subject?: string | null;
  source: 'ncert' | 'global' | string;
  chapter_title: string;
  chapter_number?: number | null;
  quiz_score?: number | null;
  completed_at?: string | null;
}

/** Per-subject completion progress: chapters finished / total chapters. */
export interface StudentTopicProgress {
  source: 'ncert' | 'global' | string;
  label: string;
  finished: number;
  total: number;
}

export const getStudentTopicCompletions = async (
  token: string,
  studentId: number
): Promise<{ completions: StudentTopicCompletion[]; progress: StudentTopicProgress[] }> => {
  try {
    const response = await API.get<{
      completions: StudentTopicCompletion[];
      progress: StudentTopicProgress[];
    }>(`/teachers/me/students/${studentId}/topic-completions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching student topic completions:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch topic completions');
  }
};

export const getReinforcementFlags = async (
  token: string,
  grade: number,
  section?: string | null
): Promise<{ flags: ReinforcementFlag[] }> => {
  try {
    const params: Record<string, string | number> = { grade };
    if (section) params.section = section;
    const response = await API.get<{ flags: ReinforcementFlag[] }>('/teachers/me/reinforcement-flags', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching reinforcement flags:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch reinforcement flags');
  }
};

export const createReinforcementFlags = async (
  token: string,
  body: CreateReinforcementFlagsBody
): Promise<{ flags: ReinforcementFlag[] }> => {
  try {
    const response = await API.post<{ flags: ReinforcementFlag[] }>(
      '/teachers/me/reinforcement-flags',
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Error creating reinforcement flags:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to flag for reinforcement');
  }
};

export const resolveReinforcementFlag = async (
  token: string,
  id: number
): Promise<{ ok: boolean }> => {
  try {
    const response = await API.delete<{ ok: boolean }>(`/teachers/me/reinforcement-flags/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error resolving reinforcement flag:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to clear flag');
  }
};

// --- Class reinforcement trend (weekly "got it" movement), teacher-auth ---
// How the class's grasp of each week's topics has trended over recent weeks.

export interface ReinforcementTrendWeek {
  week_start: string; // YYYY-MM-DD (Monday)
  got: number;
  partly: number;
  notyet: number;
  total: number;
}

export interface ReinforcementTrend {
  weeks: ReinforcementTrendWeek[]; // oldest→newest, up to 8
  this_week: ReinforcementTrendWeek | null;
  last_week: ReinforcementTrendWeek | null;
  week_delta_got_pct: number | null; // change in "% got it" vs last week
  month_delta_got_pct: number | null; // change vs ~a month ago
}

export const getReinforcementTrend = async (
  token: string,
  grade: number,
  section?: string | null
): Promise<ReinforcementTrend> => {
  try {
    const params: Record<string, string | number> = { grade };
    if (section) params.section = section;
    const response = await API.get<ReinforcementTrend>('/teachers/me/reinforcement-trend', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching reinforcement trend:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch reinforcement trend');
  }
};

// --- Class weekly plan ("what we taught this week"), teacher-auth ---

export interface ClassWeeklyPlanHomeworkItem {
  subject?: string | null; // 'maths' | 'science' | 'social' | null (null = any subject)
  task: string;
  due_date?: string | null; // YYYY-MM-DD
}

export interface ClassWeeklyPlanExam {
  date: string; // YYYY-MM-DD
  subject?: string | null;
  title?: string | null; // e.g. "Unit test — Chapters 1–4" (≤120 chars)
}

export interface ClassWeeklyPlan {
  grade: number;
  section?: string | null;
  topics: PlannedTopic[];
  mode: 'review' | 'preteach';
  homeworks?: ClassWeeklyPlanHomeworkItem[];
  exams?: ClassWeeklyPlanExam[];
  updated_at?: string | null;
}

export const getClassWeeklyPlan = async (
  token: string,
  grade: number,
  section?: string | null
): Promise<ClassWeeklyPlan> => {
  const params: Record<string, string | number> = { grade };
  if (section) params.section = section;
  const response = await API.get<ClassWeeklyPlan>('/teachers/me/class-plan', {
    params,
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

/** One weekly plan per class the teacher owns (classes with no plan yet come
 *  back with topics: [], homeworks: [], exams: []). */
export const getTeacherClassPlans = async (
  token: string
): Promise<{ plans: ClassWeeklyPlan[] }> => {
  try {
    const response = await API.get<{ plans: ClassWeeklyPlan[] }>('/teachers/me/class-plans', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching class plans:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch class plans');
  }
};

/** FULL-STATE save: always GET the plan first and POST the whole state back —
 *  omitting `exams`/`homeworks` clears them on the server. */
export const saveClassWeeklyPlan = async (
  token: string,
  plan: {
    grade: number;
    section?: string | null;
    /** Optional: topics are now ticked off via syllabus-progress; the class-plan
     *  server defaults `topics` to [] when omitted. */
    topics?: PlannedTopic[];
    mode?: 'review' | 'preteach';
    homeworks?: ClassWeeklyPlanHomeworkItem[];
    exams?: ClassWeeklyPlanExam[];
  }
): Promise<ClassWeeklyPlan> => {
  try {
    const response = await API.post<ClassWeeklyPlan>('/teachers/me/class-plan', plan, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Error saving class weekly plan:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to save weekly plan');
  }
};

export interface StudentAnalysisResponse {
  analysis?: string | null;
  status: AnalysisStatus;
  job_id?: string | null;
  computed_at?: string | null;
}

export const analyzeStudentConversations = async (
  studentId: number,
  forceRefresh = false
): Promise<StudentAnalysisResponse> => {
  try {
    const params: Record<string, boolean> = {};
    if (forceRefresh) {
      params.force_refresh = true;
    }
    const response = await API.post<StudentAnalysisResponse>(`/students/${studentId}/analysis`, {}, { params });
    return response.data;
  } catch (error: any) {
    console.error("Error analyzing student conversations:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to analyze student conversations');
  }
};

export const getAnalysisJobStatus = async (jobId: string): Promise<AnalysisJobStatusResponse> => {
  try {
    const response = await API.get<AnalysisJobStatusResponse>(`/students/analysis-jobs/${jobId}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching analysis job ${jobId} status:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch analysis status');
  }
};


export interface StreamCallbacks {
  onToken: (text: string) => void;
  onDone: (data: { message_id: number; curiosity_score: number | null }) => void;
  onError: (message: string) => void;
}

/**
 * Send a message and consume the AI response as an SSE stream (v2 hot path).
 * Replaces sendMessage + pollAiResponse: the /stream endpoint saves the user
 * message server-side and streams the reply token-by-token.
 */
/**
 * Upload a student image (photo of a problem / their work, or a notepad
 * drawing) for the given conversation. Returns the stored image id, which the
 * caller attaches to the next chat turn via `streamMessage`.
 *
 * Uses plain axios (not the shared JSON `API` instance, which would mangle the
 * multipart body) — same pattern as `extractPdfTopics`.
 */
export const uploadImage = async (
  conversationId: number,
  file: File | Blob,
): Promise<{ image_id: string; url: string }> => {
  const baseURL = (process.env.REACT_APP_BACKEND_BASE_URL || '') + '/api';

  const formData = new FormData();
  // Name the part so the browser sends a filename; the backend derives the
  // extension from the content-type, not the name.
  formData.append('image', file, 'upload.jpg');
  formData.append('conversation_id', String(conversationId));

  try {
    // Base axios, not `API`: its JSON content-type default would break the
    // multipart upload. So auth + token renewal are done here instead.
    const response = await _withStudentAuth((authorization) =>
      axios.post(`${baseURL}/images/upload`, formData, {
        headers: authorization ? { Authorization: authorization } : {},
        validateStatus: () => true,
      }),
    );
    if (response.status >= 400) throw { response };
    return response.data;
  } catch (error: any) {
    console.error('Image upload error:', error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    const message = typeof detail === 'string' ? detail : (detail?.[0]?.msg || 'Image upload failed');
    throw new Error(message);
  }
};

export const streamMessage = async (
  conversationId: number,
  content: string,
  callbacks: StreamCallbacks,
  attachedImageId?: string | null,
  imageMode?: string | null,
): Promise<void> => {
  const base = process.env.REACT_APP_BACKEND_BASE_URL || '';

  const body: { content: string; attached_image_id?: string; image_mode?: string } = { content };
  if (attachedImageId) {
    body.attached_image_id = attachedImageId;
    if (imageMode) body.image_mode = imageMode;
  }

  let resp: Response;
  try {
    resp = await _withStudentAuth((authorization) =>
      fetch(`${base}/api/conversations/${conversationId}/messages/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authorization ? { Authorization: authorization } : {}),
        },
        body: JSON.stringify(body),
      }),
    );
  } catch (e: any) {
    callbacks.onError(e?.message || 'Network error');
    return;
  }

  if (!resp.ok || !resp.body) {
    callbacks.onError(`Request failed (HTTP ${resp.status})`);
    return;
  }

  const processFrame = (frame: string) => {
    let eventType = 'message';
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.startsWith('data: ') ? line.slice(6) : line.slice(5));
      }
    }
    const dataStr = dataLines.join('\n');
    if (eventType === 'done') {
      try {
        callbacks.onDone(JSON.parse(dataStr));
      } catch {
        callbacks.onDone({ message_id: -1, curiosity_score: null });
      }
    } else if (eventType === 'error') {
      let msg = 'AI service error';
      try { msg = JSON.parse(dataStr).message || msg; } catch { /* keep default */ }
      callbacks.onError(msg);
    } else if (dataStr) {
      callbacks.onToken(dataStr);
    }
  };

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      if (frame.trim()) processFrame(frame);
    }
  }
};

export const getConversationMessages = async (conversationId: number): Promise<ChatHistory> => {
  try {
    const response = await API.get<ChatHistory>(`/conversations/${conversationId}/messages`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching messages:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to get messages');
  }
};

export const getConversationLookup = async (conversationId: number): Promise<ConversationLookupResponse> => {
  try {
    const response = await API.get<ConversationLookupResponse>(`/students/conversations/${conversationId}/lookup`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching conversation lookup:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to lookup conversation');
  }
};


export const listConversations = async (
  tags?: string[],
  tagMode?: 'any' | 'all'
): Promise<ConversationSummary[]> => {
  try {
    const params: Record<string, string> = {};
    if (tags && tags.length > 0) {
      params.tags = tags.join(',');
    }
    if (tagMode) {
      params.tag_mode = tagMode;
    }
    const response = await API.get<ConversationSummary[]>('/conversations', { params });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching conversations:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to get conversations');
  }
};

export const createConversation = async (title?: string): Promise<ConversationCreateResponse> => {
  try {
    const payload = title ? { title } : {};
    const response = await API.post<ConversationCreateResponse>('/conversations', payload);
    return response.data;
  } catch (error: any) {
    console.error("Error creating conversation:", error.response?.data || error.message);
    // Preserve status code for 503 errors (preparation timeout)
    const err: any = new Error(error.response?.data?.detail || 'Failed to create conversation');
    err.status = error.response?.status;
    throw err;
  }
};

// Add a function to verify auth status by calling /auth/me
export const verifyAuthStatus = async (): Promise<User> => {
  try {
    // The interceptor automatically adds the Authorization header
    const response = await API.get<User>('/auth/me'); 
    return response.data;
  } catch (error: any) {
    console.error("Auth status verification failed:", error.response?.data || error.message);
    // Re-throw the error so the caller (AuthProvider) knows verification failed
    throw new Error(error.response?.data?.detail || 'Session verification failed');
  }
};

// Persist the student's preferred coach-response language ('en' | 'hi').
// Returns the updated user so the caller can refresh AuthContext + storage.
export const updateLanguageApi = async (language: 'en' | 'hi'): Promise<User> => {
  try {
    const response = await API.patch<User>('/auth/me/language', { language });
    return response.data;
  } catch (error: any) {
    console.error('Failed to update language:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to update language');
  }
};

export const updateConversationTitleApi = async (conversationId: number, newTitle: string): Promise<Conversation> => {
  try {
    const response = await API.put<Conversation>(`/conversations/${conversationId}/title`, { title: newTitle });
    return response.data;
  } catch (error: any) {
    console.error(`Error updating conversation title for ID ${conversationId}:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to update conversation title');
  }
};

// New function to get pipeline steps for an AI message
export const getPipelineSteps = async (aiMessageId: number | string): Promise<any[]> => {
  try {
    // The interceptor automatically adds the Authorization header
    const response = await API.get<any[]>(`/messages/${aiMessageId}/pipeline_steps`);
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching pipeline steps for AI message ID ${aiMessageId}:`, error.response?.data || error.message);
    // If the error is a 404, or if no specific steps are found, the backend returns [] which is fine.
    // For other errors, we might want to throw or return a specific error object.
    // For now, let's re-throw to be handled by the caller, similar to other functions.
    throw new Error(error.response?.data?.detail || `Failed to fetch pipeline steps for message ${aiMessageId}`);
  }
};

// Intent router + advisor debug trace for one AI message. Returns {} when no
// trace was persisted. Used by the inline /chat?debug=1 panel.
export interface IntentRouterTrace {
  message_id?: number;
  inputs?: {
    query?: string;
    conversation_history?: string;
    core_theme?: string;
    student_grade_description?: string;
  };
  state?: {
    interest_signal?: string;
    student_intent?: string;
    topic_action?: string;
    question_policy?: string;
    response_goal?: string;
    reason_short?: string;
  };
  guidance_block?: string;
  router_system_prompt?: string;
  router_user_prompt?: string;
  // Advisor snapshot: what the async strategist had written by the time THIS
  // turn loaded context. rendered_block is the formatted string injected as
  // {{ADVISOR_NOTES}} into the system prompt; raw_notes is the structured
  // JSON the advisor returned (core_theme, on_theme, next_move, element_coverage,
  // interest_level, tone, true_curiosity_score, etc.). Both empty on turn 1.
  advisor?: {
    rendered_block?: string;
    raw_notes?: Record<string, any>;
  };
  // Progress Review decision — pulled out of advisor.raw_notes for prominent
  // panel rendering. `due` = advisor flagged THIS turn as a cadence checkpoint;
  // `directive_text` = the exact block injected into the system prompt this
  // turn (empty when not due).
  progress_review?: {
    due?: boolean;                     // back-compat: true when next_action == "progress_review"
    next_action?: string;              // stay_drill | switch_angle | progress_review | pivot_element |
                                       // re_anchor | bridge_to_related | offer_quiz | ask_student | wrap_up
    forced_by_floor?: boolean;         // true when the Python backstop flipped stay_drill → progress_review
    bridge_target?: string;            // only when next_action == "bridge_to_related"
    bridge_reason?: string;
    quiz_focus?: string;               // only when next_action == "offer_quiz"
    re_anchor_target?: string;         // only when next_action == "re_anchor"
    tangent_acknowledged?: string;
    student_category?: string;         // TVG A/B/C learner archetype: bright | hardworking | disengaged
    category_evidence?: string;
    rescue_problem?: string;           // only when next_action == "rescue_mode"
    struggle_signal?: string;          // only when next_action == "rescue_mode"
    active_intent_mode?: string;       // committed student_intent steering the coach this turn
                                       // ("" | unknown | exam_revision | pure_curiosity |
                                       //  classroom_followup | casual_question)
    directive_text?: string;
  };
  // Advisor's verdict on the student's prior exposure to this topic.
  // state is one of "" | "unknown" | "never_learned" | "forgotten" | "partly_solid".
  // evidence is a short quote/observation justifying the label.
  knowledge?: {
    state?: string;
    evidence?: string;
  };
}

export const getIntentRouterTrace = async (aiMessageId: number | string): Promise<IntentRouterTrace> => {
  try {
    const response = await API.get<IntentRouterTrace>(`/messages/${aiMessageId}/intent_router`);
    return response.data || {};
  } catch (error: any) {
    // 404 / 403 / 500 — just hide the panel rather than blow up the chat UI.
    console.warn(`Intent router trace unavailable for message ${aiMessageId}:`, error.response?.data || error.message);
    return {};
  }
};

// --- Prompt Versioning API Methods ---

// Get a list of all prompts
export const getPrompts = async () => {
  try {
    const response = await API.get('/prompts');
    return response.data;
  } catch (error) {
    console.error("Error fetching prompts:", error);
    throw new Error("Failed to fetch prompts");
  }
};

// Create a new prompt
export const createPrompt = async (name: string, description?: string, promptPurpose?: string | null) => {
  try {
    const response = await API.post('/prompts', {
      name,
      description: description || null,
      prompt_purpose: promptPurpose || null
    });
    return response.data;
  } catch (error) {
    console.error("Error creating prompt:", error);
    throw new Error("Failed to create prompt");
  }
};

// Update an existing prompt
export const updatePrompt = async (promptId: number, name?: string, description?: string, promptPurpose?: string | null) => {
  try {
    const payload: any = {};
    if (name !== undefined) payload.name = name;
    if (description !== undefined) payload.description = description;
    if (promptPurpose !== undefined) payload.prompt_purpose = promptPurpose;
    
    const response = await API.put(`/prompts/${promptId}`, payload);
    return response.data;
  } catch (error) {
    console.error(`Error updating prompt ${promptId}:`, error);
    throw new Error(`Failed to update prompt ${promptId}`);
  }
};

// Get a specific prompt by name or ID
export const getPrompt = async (nameOrId: string | number) => {
  try {
    const response = await API.get(`/prompts/${nameOrId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching prompt ${nameOrId}:`, error);
    throw new Error(`Failed to fetch prompt ${nameOrId}`);
  }
};

// Get all versions of a specific prompt
export const getPromptVersions = async (nameOrId: string | number) => {
  try {
    const response = await API.get(`/prompts/${nameOrId}/versions`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching versions for prompt ${nameOrId}:`, error);
    throw new Error(`Failed to fetch versions for prompt ${nameOrId}`);
  }
};

// Get the active version of a specific prompt
export const getActivePromptVersion = async (nameOrId: string | number) => {
  try {
    const response = await API.get(`/prompts/${nameOrId}/versions/active`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching active version for prompt ${nameOrId}:`, error);
    throw new Error(`Failed to fetch active version for prompt ${nameOrId}`);
  }
};

// Create a new version of a prompt
export const createPromptVersion = async (promptId: number | string, promptText: string) => {
  try {
    const response = await API.post(`/prompts/${promptId}/versions`, {
      prompt_text: promptText
    });
    return response.data;
  } catch (error) {
    console.error(`Error creating new version for prompt ${promptId}:`, error);
    throw new Error(`Failed to create new version for prompt ${promptId}`);
  }
};

// Set a specific version as active
export const setActivePromptVersion = async (promptId: number | string, versionId: number) => {
  try {
    const response = await API.post(`/prompts/${promptId}/versions/set-active/`, {
      version_id: versionId
    });
    return response.data;
  } catch (error) {
    console.error(`Error setting active version ${versionId} for prompt ${promptId}:`, error);
    throw new Error(`Failed to set active version for prompt ${promptId}`);
  }
};

// Set a specific version as production
export const setProductionPromptVersion = async (promptId: number | string, versionNumber: number) => {
  try {
    const response = await API.post(`/prompts/${promptId}/versions/${versionNumber}/set-production`);
    return response.data;
  } catch (error) {
    console.error(`Error setting production version ${versionNumber} for prompt ${promptId}:`, error);
    throw new Error(`Failed to set production version for prompt ${promptId}`);
  }
};

// Unset production flag from a version
export const unsetProductionPromptVersion = async (promptId: number | string, versionNumber: number) => {
  try {
    const response = await API.delete(`/prompts/${promptId}/versions/${versionNumber}/unset-production`);
    return response.data;
  } catch (error) {
    console.error(`Error unsetting production version ${versionNumber} for prompt ${promptId}:`, error);
    throw new Error(`Failed to unset production version for prompt ${promptId}`);
  }
};

export const submitFeedback = async (feedbackData: { [key: string]: any }): Promise<any> => {
  try {
    const response = await API.post('/feedback/', {
      feedback_data: feedbackData,
    });
    return response.data;
  } catch (error: any) {
    console.error("Error submitting feedback:", error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to submit feedback');
  }
};

export const getConversationMemory = async (conversationId: number | string): Promise<any> => {
  try {
    const response = await API.get(`/conversations/${conversationId}/memory`);
    return response.data;
  } catch (error: any) {
    console.error(`Error fetching memory for conversation ID ${conversationId}:`, error.response?.data || error.message);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(error.response?.data?.detail || `Failed to fetch memory for conversation ${conversationId}`);
  }
};

// --- PDF Topic Extractor (teacher-side) ---
//
// Teachers upload PDFs, get extracted topic sections, and (later) assign
// extractions to one or more of their classes via /assignments.

export interface PdfSection {
  title: string;
  content: string;
}

export interface PdfExtraction {
  id: number;
  file_name: string;
  page_count?: number | null;
  status: 'running' | 'completed' | 'failed';
  sections?: PdfSection[] | null;
  // Which section indices the teacher curated for student delivery. null = all.
  selected_section_indices?: number[] | null;
  error_message?: string | null;
  created_at: string;
  completed_at?: string | null;
}

export interface PdfExtractionSummary {
  id: number;
  file_name: string;
  page_count?: number | null;
  status: 'running' | 'completed' | 'failed';
  section_count?: number | null;
  created_at: string;
  completed_at?: string | null;
}

export const extractPdfTopics = async (token: string, file: File): Promise<PdfExtraction> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    // Bypass shared API (JSON content-type would mangle multipart). Send the
    // teacher JWT explicitly.
    const baseURL = (process.env.REACT_APP_BACKEND_BASE_URL || '') + '/api';
    const response = await axios.post(`${baseURL}/pdf-topics/extract`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('PDF extraction error:', error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    const message = typeof detail === 'string' ? detail : (detail?.[0]?.msg || 'PDF extraction failed');
    throw new Error(message);
  }
};

export const listPdfExtractions = async (
  token: string,
  options?: { limit?: number; offset?: number }
): Promise<PdfExtractionSummary[]> => {
  try {
    const response = await API.get<PdfExtractionSummary[]>('/pdf-topics', {
      headers: { Authorization: `Bearer ${token}` },
      params: { limit: options?.limit ?? 50, offset: options?.offset ?? 0 },
    });
    return response.data;
  } catch (error: any) {
    console.error('List PDF extractions error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch PDF extractions');
  }
};

export const getPdfExtraction = async (token: string, id: number): Promise<PdfExtraction> => {
  try {
    const response = await API.get<PdfExtraction>(`/pdf-topics/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error(`Get PDF extraction ${id} error:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch PDF extraction');
  }
};

// --- Assignments (teacher → classes) ---

export interface PdfAssignmentInfo {
  grade: number;
  section?: string | null;
  created_at: string;
}

export const listPdfAssignments = async (
  token: string,
  extractionId: number
): Promise<PdfAssignmentInfo[]> => {
  try {
    const response = await API.get<PdfAssignmentInfo[]>(`/pdf-topics/${extractionId}/assignments`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('List PDF assignments error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch assignments');
  }
};

// --- Student: assigned topics ---

export const listAssignedTopics = async (): Promise<PdfExtractionSummary[]> => {
  try {
    const response = await API.get<PdfExtractionSummary[]>('/topics/assigned');
    return response.data;
  } catch (error: any) {
    console.error('List assigned topics error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch assigned topics');
  }
};

export const getAssignedTopic = async (id: number): Promise<PdfExtraction> => {
  try {
    const response = await API.get<PdfExtraction>(`/topics/assigned/${id}`);
    return response.data;
  } catch (error: any) {
    console.error(`Get assigned topic ${id} error:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch topic');
  }
};

// --- Student: grade curriculum (NCERT chapters for the topic picker) ---

export const getGradeChapters = async (): Promise<GradeChaptersResponse> => {
  try {
    const response = await API.get<GradeChaptersResponse>('/curriculum/chapters');
    return response.data;
  } catch (error: any) {
    console.error('Get grade chapters error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch chapters');
  }
};

export interface StemModuleImage {
  page: number;
  url: string; // absolute URL, ready for <img src>
}

/** Student: page images for a STEM module (by topic title) — the scrollable
 *  side panel while they learn it. Returns [] for non-STEM / un-rendered topics. */
export const getStemImages = async (topic: string): Promise<StemModuleImage[]> => {
  if (!topic || !topic.trim()) return [];
  try {
    const res = await API.get<{ images: StemModuleImage[] }>('/curriculum/stem-images', {
      params: { topic },
    });
    const base = process.env.REACT_APP_BACKEND_BASE_URL || '';
    return (res.data.images || []).map((im) => ({ ...im, url: `${base}${im.url}` }));
  } catch {
    return [];
  }
};

/** Student: page images (with the module title) for the module the teacher
 *  PUSHED as today's session (resolved server-side). Lets the side panel open
 *  when the student just opened the coach on a pushed module, without picking it. */
export const getPushedStemImages = async (): Promise<{ title: string | null; images: StemModuleImage[] }> => {
  try {
    const res = await API.get<{ title: string | null; images: StemModuleImage[] }>('/curriculum/pushed-stem-images');
    const base = process.env.REACT_APP_BACKEND_BASE_URL || '';
    return {
      title: res.data.title || null,
      images: (res.data.images || []).map((im) => ({ ...im, url: `${base}${im.url}` })),
    };
  } catch {
    return { title: null, images: [] };
  }
};

/**
 * Student: start a NEW chat anchored to an assigned topic. The backend creates
 * the conversation with topic_context set, so the AI stays on the topic for
 * every turn. Returns the new conversation id; the caller should navigate to
 * /chat and let ChatContext select it.
 */
// --- Conversation quizzes ---

export const getConversationQuizEligibility = async (
  conversationId: number,
  signal?: AbortSignal,
): Promise<QuizEligibility> => {
  try {
    const response = await API.get<QuizEligibility>(
      `/conversations/${conversationId}/quiz/eligibility`,
      { signal },
    );
    return response.data;
  } catch (error: any) {
    if (error?.name === 'CanceledError' || axios.isCancel?.(error)) {
      throw error;
    }
    console.error(`Quiz eligibility ${conversationId} error:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to check quiz eligibility');
  }
};

export const getConversationQuiz = async (
  conversationId: number,
  signal?: AbortSignal,
): Promise<ConversationQuiz> => {
  try {
    const response = await API.get<ConversationQuiz>(
      `/conversations/${conversationId}/quiz`,
      { signal },
    );
    return response.data;
  } catch (error: any) {
    if (error?.name === 'CanceledError' || axios.isCancel?.(error)) {
      throw error;
    }
    if (error.response?.status === 404) {
      throw new Error('QUIZ_NOT_FOUND');
    }
    console.error(`Get quiz ${conversationId} error:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to fetch quiz');
  }
};

export const generateConversationQuiz = async (
  conversationId: number,
  regenerate = false,
  /** Optional cancellation signal. If aborted, axios throws a CanceledError
   *  with `name === 'CanceledError'`; callers should treat that as a no-op
   *  rather than a real error. */
  signal?: AbortSignal
): Promise<ConversationQuiz> => {
  try {
    const response = await API.post<ConversationQuiz>(
      `/conversations/${conversationId}/quiz`,
      null,
      {
        params: regenerate ? { regenerate: true } : undefined,
        signal,
      }
    );
    return response.data;
  } catch (error: any) {
    // Don't log/wrap cancellations as failures.
    if (error?.name === 'CanceledError' || axios.isCancel?.(error)) {
      throw error;
    }
    console.error(`Generate quiz ${conversationId} error:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to generate quiz');
  }
};

export const submitConversationQuiz = async (
  conversationId: number,
  answers: Record<string, string>
): Promise<QuizSubmitResponse> => {
  try {
    const response = await API.post<QuizSubmitResponse>(`/conversations/${conversationId}/quiz/submit`, {
      answers,
    });
    return response.data;
  } catch (error: any) {
    console.error(`Submit quiz ${conversationId} error:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to submit quiz');
  }
};

// --- Topic assessments: TEACHER (generate from selected STEM topics + edit) ---

/** STEM topic catalog (modules → topics) the teacher picks from. */
export const getAssessmentTopics = async (
  token: string,
  grade: number
): Promise<TopicCatalog> => {
  try {
    const response = await API.get<TopicCatalog>('/teachers/me/assessments/topics', {
      params: { grade },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Get assessment topics error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to load topics');
  }
};

/** Generate ONE combined assessment from the selected topics. */
export const generateAssessment = async (
  token: string,
  grade: number,
  topicKeys: string[],
  title?: string
): Promise<TeacherAssessmentDetail> => {
  try {
    const response = await API.post<TeacherAssessmentDetail>(
      '/teachers/me/assessments/generate',
      { grade, topic_keys: topicKeys, title },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Generate assessment error:', error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    const msg = typeof detail === 'string' ? detail : (detail?.[0]?.msg || 'Failed to generate assessment');
    throw new Error(msg);
  }
};

export const deleteTeacherAssessment = async (
  token: string,
  assessmentId: number
): Promise<void> => {
  try {
    await API.delete(`/teachers/me/assessments/${assessmentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error: any) {
    console.error('Delete assessment error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to delete assessment');
  }
};

export const getTeacherAssessments = async (
  token: string,
  grade: number
): Promise<TeacherAssessmentList> => {
  try {
    const response = await API.get<TeacherAssessmentList>('/teachers/me/assessments', {
      params: { grade },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Get teacher assessments error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to load assessments');
  }
};

export const getTeacherAssessment = async (
  token: string,
  assessmentId: number
): Promise<TeacherAssessmentDetail> => {
  try {
    const response = await API.get<TeacherAssessmentDetail>(
      `/teachers/me/assessments/${assessmentId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Get teacher assessment error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to load assessment');
  }
};

export const saveTeacherAssessment = async (
  token: string,
  assessmentId: number,
  sections: TeacherAssessmentSection[]
): Promise<TeacherAssessmentDetail> => {
  try {
    const response = await API.put<TeacherAssessmentDetail>(
      `/teachers/me/assessments/${assessmentId}`,
      { sections },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Save teacher assessment error:', error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    const msg = typeof detail === 'string' ? detail : (detail?.[0]?.msg || 'Failed to save assessment');
    throw new Error(msg);
  }
};

export const regenerateTeacherAssessment = async (
  token: string,
  assessmentId: number
): Promise<TeacherAssessmentDetail> => {
  try {
    const response = await API.post<TeacherAssessmentDetail>(
      `/teachers/me/assessments/${assessmentId}/regenerate`,
      null,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Regenerate assessment error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to regenerate assessment');
  }
};

// --- Topic assessments: STUDENT (unlocked on module completion, AI-graded) ---

export const getAvailableAssessments = async (): Promise<AvailableAssessment[]> => {
  try {
    const response = await API.get<AvailableAssessment[]>('/assessments/available');
    return response.data;
  } catch (error: any) {
    console.error('Get available assessments error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to load assessments');
  }
};

export const getStudentAssessment = async (
  assessmentId: number
): Promise<StudentAssessmentDetail> => {
  try {
    const response = await API.get<StudentAssessmentDetail>(`/assessments/${assessmentId}`);
    return response.data;
  } catch (error: any) {
    console.error('Get student assessment error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to load assessment');
  }
};

export const submitAssessment = async (
  assessmentId: number,
  answers: Record<string, string>
): Promise<AssessmentResult> => {
  try {
    const response = await API.post<AssessmentResult>(`/assessments/${assessmentId}/submit`, {
      answers,
    });
    return response.data;
  } catch (error: any) {
    console.error('Submit assessment error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to submit assessment');
  }
};

export const getAssessmentResult = async (
  assessmentId: number
): Promise<AssessmentResult> => {
  try {
    const response = await API.get<AssessmentResult>(`/assessments/${assessmentId}/result`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) throw new Error('NO_SUBMISSION');
    console.error('Get assessment result error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to load result');
  }
};

// ── Homework ─────────────────────────────────────────────────────────────────
// Student calls: the request interceptor supplies `Bearer <user.id>` automatically.

export const getMyHomework = async (): Promise<StudentHomeworkItem[]> => {
  try {
    const response = await API.get<StudentHomeworkItem[]>('/homework/mine');
    return response.data;
  } catch (error: any) {
    console.error('Get my homework error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to load homework');
  }
};

export const setHomeworkDone = async (
  assignmentId: number,
  done: boolean
): Promise<MarkHomeworkDoneResponse> => {
  try {
    const path = `/homework/${assignmentId}/complete`;
    const response = done
      ? await API.post<MarkHomeworkDoneResponse>(path)
      : await API.delete<MarkHomeworkDoneResponse>(path);
    return response.data;
  } catch (error: any) {
    console.error('Set homework done error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to update homework');
  }
};

// ── Projects (student-authored) ──────────────────────────────────────────────
// Student calls: the request interceptor supplies `Bearer <user.id>` automatically.

export const getMyProjects = async (): Promise<StudentProjectItem[]> => {
  try {
    const response = await API.get<StudentProjectItem[]>('/projects/mine');
    return response.data;
  } catch (error: any) {
    console.error('Get my projects error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to load projects');
  }
};

// Multipart create — plain axios (the shared JSON `API` instance would mangle the
// FormData body), same pattern as uploadImage.
export const createProject = async (
  title: string,
  description?: string,
  file?: File | Blob | null,
): Promise<StudentProjectItem> => {
  const baseURL = (process.env.REACT_APP_BACKEND_BASE_URL || '') + '/api';

  const formData = new FormData();
  formData.append('title', title);
  if (description) formData.append('description', description);
  if (file) formData.append('image', file, 'project.jpg');

  try {
    const response = await _withStudentAuth((authorization) =>
      axios.post<StudentProjectItem>(`${baseURL}/projects`, formData, {
        headers: authorization ? { Authorization: authorization } : {},
        validateStatus: () => true,
      }),
    );
    if (response.status >= 400) throw { response };
    return response.data;
  } catch (error: any) {
    console.error('Create project error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to share project');
  }
};

export const deleteProject = async (projectId: number): Promise<void> => {
  try {
    await API.delete(`/projects/${projectId}`);
  } catch (error: any) {
    console.error('Delete project error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to delete project');
  }
};

// Teacher call: pass the JWT explicitly (matches getClassWeeklyPlan).
export const getClassHomeworkStats = async (
  token: string,
  grade: number,
  section?: string | null
): Promise<ClassHomeworkStats> => {
  const params: Record<string, string | number> = { grade };
  if (section) params.section = section;
  try {
    const response = await API.get<ClassHomeworkStats>('/teachers/me/homework-stats', {
      params,
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error('Get class homework stats error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to load homework stats');
  }
};

export const startTopicChat = async (extractionId: number): Promise<{ conversation_id: number }> => {
  try {
    const response = await API.post<{ conversation_id: number }>(
      `/topics/assigned/${extractionId}/start-chat`
    );
    return response.data;
  } catch (error: any) {
    console.error(`Start topic chat ${extractionId} error:`, error.response?.data || error.message);
    throw new Error(error.response?.data?.detail || 'Failed to start chat');
  }
};

export const replacePdfAssignments = async (
  token: string,
  extractionId: number,
  classes: TeacherClassItem[],
  selectedSectionIndices: number[] | null
): Promise<PdfAssignmentInfo[]> => {
  try {
    const response = await API.put<PdfAssignmentInfo[]>(
      `/pdf-topics/${extractionId}/assignments`,
      { classes, selected_section_indices: selectedSectionIndices },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error: any) {
    console.error('Replace PDF assignments error:', error.response?.data || error.message);
    const detail = error.response?.data?.detail;
    const msg = typeof detail === 'string' ? detail : (detail?.[0]?.msg || 'Failed to save assignments');
    throw new Error(msg);
  }
};


// --- Curiosity (entry) assessment ---------------------------------------------
// A one-time self-report survey each student in grades 6-9 takes before their
// first coach conversation. No right answers: the server turns the answers into
// a 0-100 curiosity score and a level, which reach the teacher dashboard and the
// monitor. The student is never shown their score.

export interface EntryAssessmentQuestion {
  id: string;
  type: 'scale' | 'text';
  text: string;
  hint?: string | null;
  required: boolean;
  options?: { id: string; text: string }[];
  max_length?: number;
}

export interface EntryAssessmentSection {
  id: string;
  instructions?: string | null;
  questions: EntryAssessmentQuestion[];
}

export interface EntryAssessmentPaper {
  paper_id: string;
  sample: boolean;
  language: 'en' | 'hi';
  title: string;
  instructions: string;
  sections: EntryAssessmentSection[];
}

export interface EntryAssessmentState {
  status: 'pending' | 'completed' | 'not_required';
  paper?: EntryAssessmentPaper | null;
  submitted_at?: string | null;
}

export interface EntryAssessmentSubmitResult {
  status: 'completed';
  submitted_at: string;
}

export const getEntryAssessment = async (language?: 'en' | 'hi'): Promise<EntryAssessmentState> => {
  try {
    const res = await API.get<EntryAssessmentState>('/entry-assessment', {
      params: language ? { language } : undefined,
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load the assessment');
  }
};

export const submitEntryAssessment = async (body: {
  paper_id: string;
  answers: Record<string, string>;
  language: 'en' | 'hi';
  started_at?: string;
}): Promise<EntryAssessmentSubmitResult> => {
  try {
    const res = await API.post<EntryAssessmentSubmitResult>('/entry-assessment/submit', body);
    return res.data;
  } catch (error: any) {
    const err: any = new Error(error.response?.data?.detail || 'Failed to submit the assessment');
    err.status = error.response?.status;
    throw err;
  }
};

export interface CuriosityLevelCount {
  key: string;
  label: string;
  count: number;
}

export interface CuriositySectionScore {
  id: string;
  label: string;
  score?: number | null;
}

export interface EntryAssessmentStudentResult {
  student_id: number;
  name?: string | null;
  roll_number?: number | null;
  section?: string | null;
  completed: boolean;
  curiosity_score?: number | null;
  curiosity_level?: string | null;
  level_label?: string | null;
  section_scores: CuriositySectionScore[];
  curious_question?: string | null;
  submitted_at?: string | null;
}

export interface ClassEntryAssessment {
  required: boolean;
  paper_id?: string | null;
  sample: boolean;
  total_students: number;
  completed: number;
  average_score?: number | null;
  levels: CuriosityLevelCount[];
  sections: CuriositySectionScore[];
  students: EntryAssessmentStudentResult[];
}

export const getClassEntryAssessment = async (
  token: string,
  school: string,
  grade: number,
  section?: string | null,
): Promise<ClassEntryAssessment> => {
  try {
    const res = await API.get<ClassEntryAssessment>('/teachers/me/entry-assessment', {
      params: { school, grade, ...(section ? { section } : {}) },
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load curiosity assessment results');
  }
};

export interface EntryAssessmentGroup {
  key: string;
  total_students: number;
  completed: number;
  average_score?: number | null;
}

export interface MonitorEntryAssessment {
  grades: number[];
  sample: boolean;
  total_students: number;
  completed: number;
  average_score?: number | null;
  levels: CuriosityLevelCount[];
  by_school: EntryAssessmentGroup[];
  by_grade: EntryAssessmentGroup[];
  by_school_grade: {
    school: string;
    grade: number;
    total_students: number;
    completed: number;
    average_score?: number | null;
  }[];
}

export const getMonitorEntryAssessment = async (token: string): Promise<MonitorEntryAssessment> => {
  try {
    const res = await API.get<MonitorEntryAssessment>('/monitor/entry-assessment', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.detail || 'Failed to load curiosity assessment.');
  }
};
